// src/pages/CargaMasiva/LoteDetalle.js
//
// Página de detalle de un lote de Carga Masiva.
// Tabs: Importar | Auditoría | Abogados | Documentos | Auditoría log
//
// Fase 2 entrega Importar + Auditoría (básica).
// Fases siguientes completan Abogados (sorteo/manual) y Documentos (PDFs).

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container, Box, Paper, Typography, Button, IconButton, Tooltip, Tabs, Tab,
    Chip, Alert, Table, TableHead, TableBody, TableRow, TableCell,
    TableContainer, CircularProgress, FormControlLabel, Checkbox, LinearProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Grid,
    InputAdornment,
} from '@mui/material';
import * as XLSX from 'xlsx';
import {
    ArrowBack as ArrowBackIcon,
    CloudUpload as CloudUploadIcon,
    Refresh as RefreshIcon,
    Description as DescriptionIcon,
    History as HistoryIcon,
    Assignment as AssignmentIcon,
    PictureAsPdf as PdfIcon,
    UploadFile as UploadFileIcon,
    CheckCircle as CheckIcon,
    Error as ErrorIcon,
    Edit as EditIcon,
    Block as BlockIcon,
    Undo as UndoIcon,
    DoneAll as DoneAllIcon,
    PlayArrow as PlayArrowIcon,
    GroupAdd as GroupAddIcon,
    PersonRemove as PersonRemoveIcon,
    Add as AddIcon,
    Send as SendIcon,
    Sync as SyncIcon,
    Draw as DrawIcon,
    Gavel as GavelIcon,
    RestartAlt as RestartAltIcon,
    Replay as ReplayIcon,
    OpenInNew as OpenInNewIcon,
    Delete as DeleteIcon,
    DataObject as DataObjectIcon,
    FileDownload as FileDownloadIcon,
    Search as SearchIcon,
    FilterAltOff as FilterAltOffIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import PdfPreviewDialog from '../../components/CargaMasiva/PdfPreviewDialog';

import {
    cargaMasivaObtenerLoteJWT,
    cargaMasivaImportarExcelJWT,
    cargaMasivaListarDetalleJWT,
    cargaMasivaAuditoriaJWT,
    cargaMasivaEditarFilaJWT,
    cargaMasivaAuditarFilaJWT,
    cargaMasivaBulkMarcarCorrectasJWT,
    cargaMasivaFinalizarAuditoriaJWT,
    cargaMasivaListarAbogadosJWT,
    cargaMasivaAbogadosLoteJWT,
    cargaMasivaSortearAbogadosJWT,
    cargaMasivaAsignarAbogadoManualJWT,
    cargaMasivaDesasignarAbogadoJWT,
    cargaMasivaFinalizarAsignacionJWT,
    cargaMasivaGenerarDocumentosJWT,
    cargaMasivaDescargarPDFBlob,
    cargaMasivaApoderadosFilaJWT,
    cargaMasivaAgregarCoApoderadoJWT,
    cargaMasivaQuitarCoApoderadoJWT,
    cargaMasivaEnviarAFirmaJWT,
    cargaMasivaSincronizarFirmasJWT,
    cargaMasivaResumenFirmaJWT,
    cargaMasivaEnviarDemandasAFirmaJWT,
    cargaMasivaSincronizarFirmasDemandaJWT,
    cargaMasivaResumenFirmaDemandaJWT,
    cargaMasivaPreviewJsonPJJWT,
    cargaMasivaEnviarAlPJJWT,
    cargaMasivaConsultarEstadoPJJWT,
    cargaMasivaResetSimulacionPJJWT,
    cargaMasivaEnviosPJJWT,
    cargaMasivaReenviarRechazadasPJJWT,
    cargaMasivaForzarRechazoSimuladoJWT,
    cargaMasivaPasarASigifJWT,
    getCombosJWT,
    cargaMasivaSubirPdfsJWT,
    cargaMasivaAsignarPdfManualJWT,
    cargaMasivaListarPdfsStagingJWT,
    cargaMasivaReprocesarPdfsStagingJWT,
    cargaMasivaExportarPDFBlob,
    cargaMasivaEliminarPdfStagingJWT,
} from '../../api/endpoints';
import usePermisosModulo from '../../hooks/usePermisosModulo';
import { parseFechaServer } from '../../utils/fechas';

const COLOR = '#6a1b9a';
const BR = 4;

const ESTADOS = {
    0: 'Borrador', 1: 'Importado', 2: 'En auditoría', 3: 'Auditado',
    // 6 lo pone la sincronización de firmas cuando están todas las cartas poder: no es el envío.
    4: 'Abogados asignados', 5: 'PDFs generados', 6: 'Cartas poder firmadas',
    7: 'Pasado a SIGIF', 8: 'Finalizado', [-1]: 'Anulado',
};

const ESTADO_OK_FILAS = {
    0: { label: 'Defectuosa', color: 'error', icon: <ErrorIcon fontSize="small" /> },
    1: { label: 'Correcta', color: 'success', icon: <CheckIcon fontSize="small" /> },
    2: { label: 'Pendiente auditar', color: 'warning' },
    3: { label: 'Excluida', color: 'default' },
    4: { label: 'Con abogado', color: 'primary' },
};

export default function LoteDetalle() {
    const { id: idLote } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [lote, setLote] = useState(null);
    const [loadingLote, setLoadingLote] = useState(true);
    const [exportandoPDF, setExportandoPDF] = useState(false);

    const handleExportarPDF = async () => {
        setExportandoPDF(true);
        try {
            const blob = await cargaMasivaExportarPDFBlob(idLote);
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 30000);
        } catch {
            enqueueSnackbar('Error al generar el PDF', { variant: 'error' });
        } finally {
            setExportandoPDF(false);
        }
    };
    const [tab, setTab] = useState('auditoria');

    const cargarLote = useCallback(async (esInicial = false) => {
        if (esInicial) setLoadingLote(true);
        try {
            const r = await cargaMasivaObtenerLoteJWT(idLote);
            if (r?.ok) setLote(r.lote);
            else enqueueSnackbar(r?.mensaje || 'Error al cargar lote', { variant: 'error' });
        } catch {
            enqueueSnackbar('Error al cargar lote', { variant: 'error' });
        } finally {
            if (esInicial) setLoadingLote(false);
        }
    }, [idLote, enqueueSnackbar]);

    // Carga inicial con spinner. Los refreshes posteriores son silenciosos (no desmontan tabs).
    useEffect(() => { cargarLote(true); }, [cargarLote]);

    // Ajustar el tab por defecto según el estado del lote:
    //  - Si es No Trib sin filas → tab "importar" (para que el usuario cargue el Excel)
    //  - En cualquier otro caso → tab "auditoria"
    useEffect(() => {
        if (!lote) return;
        const sinFilasYEsNoTrib = lote.Id_Modulo === 2 && (lote.Total_Filas || 0) === 0 && [0, 1].includes(lote.Estado);
        if (sinFilasYEsNoTrib) setTab('importar');
        else if (tab === 'importar') setTab('auditoria');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lote?.Id, lote?.Total_Filas, lote?.Id_Modulo, lote?.Estado]);

    if (loadingLote || !lote) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
                <CircularProgress sx={{ color: COLOR }} />
            </Box>
        );
    }

    if (!lote) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="error">Lote no encontrado</Alert>
                <Button onClick={() => navigate('/gestion-lotes?tab=carga-masiva')} sx={{ mt: 2 }}>Volver</Button>
            </Box>
        );
    }

    // Tab "Importar Excel" solo visible en:
    //  - Lotes NO Tributarios (Id_Modulo=2) — los Tributarios vienen de WS-DGR, no hay Excel
    //  - Y mientras no tenga filas aún (una vez importado, ya no tiene sentido)
    //  - Y en estado Borrador o Importado
    const mostrarTabImportar = lote.Id_Modulo === 2
        && (lote.Total_Filas || 0) === 0
        && [0, 1].includes(lote.Estado);

    return (
        <Box sx={{ bgcolor: '#f6f8fc', minHeight: '100vh', py: 3, px: { xs: 2, md: 3 } }}>
            <Container maxWidth={false} disableGutters>
                {/* Header */}
                <Paper sx={{ p: 2, mb: 2, borderRadius: `${BR}px`, borderLeft: `4px solid ${COLOR}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <IconButton onClick={() => navigate('/gestion-lotes?tab=carga-masiva')} size="small">
                            <ArrowBackIcon />
                        </IconButton>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: COLOR }}>
                                Lote {lote.Id}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                                <Chip size="small" label={ESTADOS[lote.Estado] || `Estado ${lote.Estado}`} sx={{ bgcolor: COLOR, color: 'white' }} />
                                <Typography variant="caption" color="text.secondary">
                                    {lote.Id_Modulo === 1 ? 'Tributarias' : 'No Tributarias'} — {lote.Reparticion_Nombre}
                                </Typography>
                                {lote.Expediente_Adm_Numero && (
                                    <Tooltip title={`${lote.Expediente_Adm_Caratula || ''} — abrir en nueva pestaña`}>
                                        <Chip size="small" icon={<DescriptionIcon fontSize="small" />}
                                            label={`Exp Adm: ${lote.Expediente_Adm_Numero}`}
                                            onClick={() => window.open(`/expediente/administrativo/${lote.Id_Expediente_Administrativo}`, '_blank')}
                                            sx={{ cursor: 'pointer', fontFamily: 'monospace' }} />
                                    </Tooltip>
                                )}
                                <Typography variant="caption" color="text.secondary">
                                    Creado por {lote.Usuario_Creador} el {new Date(lote.Fecha_Creacion).toLocaleDateString('es-AR')}
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="caption" color="text.secondary">
                                    {lote.Cantidad_Esperada ? 'Filas / Esperadas' : 'Filas'}
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: lote.Cantidad_Esperada && lote.Total_Filas < lote.Cantidad_Esperada ? '#f57c00' : 'inherit' }}>
                                    {lote.Total_Filas || 0}
                                    {lote.Cantidad_Esperada ? <Typography component="span" variant="caption" sx={{ ml: 0.5, opacity: 0.7 }}>/ {lote.Cantidad_Esperada}</Typography> : null}
                                </Typography>
                            </Box>
                            <Tooltip title="Descargar reporte PDF del lote">
                                <IconButton onClick={handleExportarPDF} disabled={exportandoPDF}
                                    sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: `${BR}px` }}>
                                    {exportandoPDF ? <CircularProgress size={18} /> : <PdfIcon sx={{ color: COLOR }} />}
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                </Paper>

                {lote.Estado >= 7 && (
                    <Alert severity="success" sx={{ mb: 2, borderRadius: `${BR}px` }} icon={<GavelIcon />}>
                        <strong>Lote cerrado</strong> — {lote.Filas_Pasadas_SIGIF || 0} expedientes fueron creados en SIGIF.
                        No se permiten más modificaciones.
                    </Alert>
                )}

                {/* Tabs */}
                <Paper sx={{ borderRadius: `${BR}px`, mb: 2 }}>
                    <Tabs
                        value={tab}
                        onChange={(_, v) => setTab(v)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            borderBottom: 1, borderColor: 'divider',
                            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 52 },
                            '& .Mui-selected': { color: COLOR + ' !important' },
                            '& .MuiTabs-indicator': { bgcolor: COLOR },
                        }}
                    >
                        {mostrarTabImportar && (
                            <Tab icon={<UploadFileIcon />} iconPosition="start" label="Importar Excel" value="importar" />
                        )}
                        <Tab icon={<DescriptionIcon />} iconPosition="start" label="Auditoría filas" value="auditoria" />
                        <Tab icon={<AssignmentIcon />} iconPosition="start" label="Abogados" value="abogados" />
                        <Tab icon={<PdfIcon />} iconPosition="start" label="Documentos" value="documentos" />
                        <Tab icon={<GavelIcon />} iconPosition="start" label="Envío PJ" value="envio" />
                        <Tab icon={<HistoryIcon />} iconPosition="start" label="Registro" value="registro" />
                    </Tabs>

                    <Box sx={{ p: 2 }}>
                        {tab === 'importar' && <TabImportar lote={lote} onImportado={cargarLote} />}
                        {tab === 'auditoria' && <TabAuditoria lote={lote} onLoteRefresh={cargarLote} />}
                        {tab === 'abogados' && <TabAbogados lote={lote} onLoteRefresh={cargarLote} />}
                        {tab === 'documentos' && <TabDocumentos lote={lote} onLoteRefresh={cargarLote} />}
                        {tab === 'envio' && <TabEnvioPJ lote={lote} onLoteRefresh={cargarLote} />}
                        {tab === 'registro' && <TabAuditoriaLog idLote={idLote} />}
                    </Box>
                </Paper>
            </Container>

        </Box>
    );
}

// ============================================================================
// TAB IMPORTAR EXCEL
// ============================================================================
function TabImportar({ lote, onImportado }) {
    const { enqueueSnackbar } = useSnackbar();
    const excelInputRef = useRef(null);
    const pdfsInputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [pdfs, setPdfs] = useState([]);
    const [reemplazar, setReemplazar] = useState(false);
    const [subiendo, setSubiendo] = useState(false);
    const [resultado, setResultado] = useState(null);

    const tieneFilas = lote.Total_Filas > 0;

    const handleUpload = async () => {
        if (!file) return;
        setSubiendo(true);
        setResultado(null);
        try {
            const r = await cargaMasivaImportarExcelJWT(lote.Id, file, { reemplazar, pdfs });
            if (r?.ok) {
                setResultado(r);
                const matched = r.pdfs?.matched?.length || 0;
                const unmatched = r.pdfs?.unmatched?.length || 0;
                const ambiguous = r.pdfs?.ambiguous?.length || 0;
                const msgPdfs = r.pdfs ? ` · ${matched} PDFs emparejados${unmatched + ambiguous > 0 ? `, ${unmatched + ambiguous} pendientes` : ''}` : '';
                enqueueSnackbar(`${r.insertadas} filas (${r.filasOk} OK, ${r.filasDefectuosas} con errores)${msgPdfs}`,
                    { variant: r.filasDefectuosas > 0 || unmatched + ambiguous > 0 ? 'warning' : 'success' });
                setFile(null);
                setPdfs([]);
                onImportado?.();
            } else {
                enqueueSnackbar(r?.mensaje || 'Error al importar', { variant: 'error' });
                if (r?.errores) setResultado({ ok: false, errores: r.errores });
            }
        } catch {
            enqueueSnackbar('Error al procesar el archivo', { variant: 'error' });
        } finally {
            setSubiendo(false);
        }
    };

    return (
        <Box>
            {tieneFilas && !reemplazar && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Este lote ya tiene {lote.Total_Filas} filas importadas. Marcá <strong>"Reemplazar filas"</strong> si querés
                    borrar lo actual y cargar un nuevo archivo.
                </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                <Paper variant="outlined" sx={{
                    p: 3, textAlign: 'center', borderRadius: `${BR}px`,
                    borderStyle: 'dashed', borderWidth: 2, flex: 1,
                    bgcolor: file ? 'rgba(106,27,154,0.05)' : 'transparent',
                }}>
                    <input
                        ref={excelInputRef} type="file" hidden accept=".xlsx,.xls"
                        onChange={e => setFile(e.target.files?.[0] || null)}
                    />
                    <CloudUploadIcon sx={{ fontSize: 44, color: file ? COLOR : 'rgba(0,0,0,0.25)' }} />
                    <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 600 }}>
                        {file ? file.name : '1. Archivo Excel'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        .xlsx con headers estándar
                    </Typography>
                    <Button
                        variant="outlined" size="small"
                        onClick={() => excelInputRef.current?.click()}
                        disabled={subiendo}
                        sx={{ borderRadius: `${BR}px`, textTransform: 'none', color: COLOR, borderColor: COLOR }}
                    >
                        {file ? 'Cambiar' : 'Elegir Excel'}
                    </Button>
                </Paper>

                <Paper variant="outlined" sx={{
                    p: 3, textAlign: 'center', borderRadius: `${BR}px`,
                    borderStyle: 'dashed', borderWidth: 2, flex: 1,
                    bgcolor: pdfs.length > 0 ? 'rgba(106,27,154,0.05)' : 'transparent',
                }}>
                    <input
                        ref={pdfsInputRef} type="file" hidden accept=".pdf" multiple
                        onChange={e => setPdfs(Array.from(e.target.files || []))}
                    />
                    <PdfIcon sx={{ fontSize: 44, color: pdfs.length > 0 ? COLOR : 'rgba(0,0,0,0.25)' }} />
                    <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 600 }}>
                        {pdfs.length > 0 ? `${pdfs.length} PDF${pdfs.length > 1 ? 's' : ''} seleccionados` : '2. PDFs de certificados'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Emparejado automático por número de certificado
                    </Typography>
                    <Button
                        variant="outlined" size="small"
                        onClick={() => pdfsInputRef.current?.click()}
                        disabled={subiendo}
                        sx={{ borderRadius: `${BR}px`, textTransform: 'none', color: COLOR, borderColor: COLOR }}
                    >
                        {pdfs.length > 0 ? 'Cambiar selección' : 'Elegir PDFs'}
                    </Button>
                </Paper>
            </Box>

            {tieneFilas && (
                <FormControlLabel
                    sx={{ mt: 2 }}
                    control={<Checkbox checked={reemplazar} onChange={e => setReemplazar(e.target.checked)} />}
                    label="Reemplazar filas existentes (borra las actuales antes de importar)"
                />
            )}

            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button
                    variant="contained"
                    disabled={!file || subiendo}
                    onClick={handleUpload}
                    sx={{ bgcolor: COLOR, '&:hover': { bgcolor: '#4a148c' }, borderRadius: `${BR}px`, textTransform: 'none', fontWeight: 600 }}
                >
                    {subiendo ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Importar'}
                </Button>
            </Box>

            {subiendo && <LinearProgress sx={{ mt: 2 }} />}

            {resultado && resultado.ok && (
                <>
                    <Alert severity={resultado.filasDefectuosas > 0 ? 'warning' : 'success'} sx={{ mt: 2 }}>
                        <Typography variant="body2">
                            <strong>{resultado.insertadas}</strong> filas importadas · <strong>{resultado.filasOk}</strong> OK · <strong>{resultado.filasDefectuosas}</strong> con errores
                        </Typography>
                    </Alert>
                    {resultado.pdfs && (
                        <Alert
                            severity={(resultado.pdfs.unmatched.length + resultado.pdfs.ambiguous.length) > 0 ? 'warning' : 'success'}
                            sx={{ mt: 2 }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Resultado PDFs:</Typography>
                            <Typography variant="body2">
                                {resultado.pdfs.matched.length} emparejados automáticamente · {resultado.pdfs.unmatched.length} sin match · {resultado.pdfs.ambiguous.length} ambiguos
                            </Typography>
                            {resultado.pdfs.unmatched.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="caption" color="text.secondary">Sin match:</Typography>
                                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                                        {resultado.pdfs.unmatched.slice(0, 10).map((p, i) =>
                                            <li key={i}><code>{p.originalName}</code> (dígitos: {p.digitosEncontrados.join(', ') || '—'})</li>)}
                                        {resultado.pdfs.unmatched.length > 10 && <li>... {resultado.pdfs.unmatched.length - 10} más</li>}
                                    </ul>
                                    <Typography variant="caption" color="text.secondary">
                                        Resolvelos manualmente en el tab "Auditoría filas" (botón "PDF" por fila).
                                    </Typography>
                                </Box>
                            )}
                        </Alert>
                    )}
                </>
            )}

            {resultado && !resultado.ok && resultado.errores && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Errores en el archivo:</Typography>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {resultado.errores.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                </Alert>
            )}
        </Box>
    );
}

// ============================================================================
// TAB AUDITORÍA DE FILAS
// ============================================================================
function TabAuditoria({ lote, onLoteRefresh }) {
    const idLote = lote.Id;
    const { enqueueSnackbar } = useSnackbar();
    const [filas, setFilas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filtroEstado, setFiltroEstado] = useState('');
    const [filaEditando, setFilaEditando] = useState(null);
    const [filaExcluir, setFilaExcluir] = useState(null);
    const [confirmFinalizarOpen, setConfirmFinalizarOpen] = useState(false);
    const [pdfsPendientes, setPdfsPendientes] = useState([]);
    const [dialogPdfsOpen, setDialogPdfsOpen] = useState(false);
    const [dialogPdfsFila, setDialogPdfsFila] = useState(null);

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await cargaMasivaListarDetalleJWT(idLote, filtroEstado);
            if (r?.ok) setFilas(r.registros || []);
            else enqueueSnackbar(r?.mensaje || 'Error al cargar filas', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [idLote, filtroEstado, enqueueSnackbar]);

    useEffect(() => { cargar(); }, [cargar]);

    // Re-cargar filas cuando cambia el total del lote (ej. se agregaron causas desde DGR)
    useEffect(() => {
        cargar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lote.Total_Filas]);

    const cargarPdfsPendientes = useCallback(async () => {
        try {
            const r = await cargaMasivaListarPdfsStagingJWT(idLote);
            if (r?.ok) setPdfsPendientes(r.pendientes || []);
        } catch {}
    }, [idLote]);

    useEffect(() => { cargarPdfsPendientes(); }, [cargarPdfsPendientes, filas.length]);

    const asignarPdfAFila = async (nombreArchivo) => {
        if (!dialogPdfsFila) return;
        try {
            const r = await cargaMasivaAsignarPdfManualJWT(idLote, dialogPdfsFila.Id, nombreArchivo);
            if (r?.ok) {
                enqueueSnackbar('PDF asignado a fila ' + dialogPdfsFila.Fila, { variant: 'success' });
                setDialogPdfsFila(null);
                cargarPdfsPendientes();
            } else {
                enqueueSnackbar(r?.mensaje || 'Error al asignar PDF', { variant: 'error' });
            }
        } catch {
            enqueueSnackbar('Error al asignar PDF', { variant: 'error' });
        }
    };

    const subirPdfsExtra = async (filesList) => {
        const pdfs = Array.from(filesList || []).filter(f => /\.pdf$/i.test(f.name));
        if (pdfs.length === 0) return;
        try {
            const r = await cargaMasivaSubirPdfsJWT(idLote, pdfs);
            if (r?.ok) {
                enqueueSnackbar(`${r.matched.length} emparejados, ${r.unmatched.length + r.ambiguous.length} pendientes`,
                    { variant: r.unmatched.length + r.ambiguous.length > 0 ? 'warning' : 'success' });
                cargarPdfsPendientes();
            } else {
                enqueueSnackbar(r?.mensaje || 'Error al subir PDFs', { variant: 'error' });
            }
        } catch {
            enqueueSnackbar('Error al subir PDFs', { variant: 'error' });
        }
    };

    const accionRapida = async (idDetalle, estadoOk, textoOk) => {
        try {
            const r = await cargaMasivaAuditarFilaJWT({ idDetalle, idLote, estadoOk, observaciones: null });
            if (r?.ok) {
                enqueueSnackbar(textoOk, { variant: 'success' });
                cargar();
                onLoteRefresh?.();
            } else enqueueSnackbar(r?.mensaje || 'Error', { variant: 'error' });
        } catch {
            enqueueSnackbar('Error al procesar', { variant: 'error' });
        }
    };

    const bulkCorrectas = async () => {
        try {
            const r = await cargaMasivaBulkMarcarCorrectasJWT(idLote);
            if (r?.ok) {
                enqueueSnackbar(`${r.afectadas} filas marcadas correctas`, { variant: 'success' });
                cargar(); onLoteRefresh?.();
            } else enqueueSnackbar(r?.mensaje || 'Error', { variant: 'error' });
        } catch {
            enqueueSnackbar('Error al marcar correctas', { variant: 'error' });
        }
    };

    const finalizarAuditoria = async () => {
        try {
            const r = await cargaMasivaFinalizarAuditoriaJWT(idLote);
            if (r?.ok) {
                enqueueSnackbar('Auditoría finalizada. Lote listo para asignar abogados.', { variant: 'success' });
                setConfirmFinalizarOpen(false);
                onLoteRefresh?.();
            } else enqueueSnackbar(r?.mensaje || 'No se puede finalizar', { variant: 'warning' });
        } catch {
            enqueueSnackbar('Error al finalizar', { variant: 'error' });
        }
    };

    const loteIncompleto = lote.Cantidad_Esperada && (lote.Total_Filas || 0) < lote.Cantidad_Esperada;
    const faltantesEsperadas = loteIncompleto ? lote.Cantidad_Esperada - (lote.Total_Filas || 0) : 0;
    const puedeFinalizar = [1, 2].includes(lote.Estado) && !loteIncompleto;
    const hayPendientes = (lote.Filas_Pendientes || 0) > 0;

    return (
        <Box>
            {loteIncompleto && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    <strong>Lote incompleto.</strong> Faltan {faltantesEsperadas} causa{faltantesEsperadas > 1 ? 's' : ''} (esperadas {lote.Cantidad_Esperada},
                    en el lote {lote.Total_Filas || 0}). No podés finalizar la auditoría hasta completarlo.
                </Alert>
            )}

            {/* Barra de acciones */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                {['', 0, 1, 2, 3, 4].map(v => {
                    const lab = v === '' ? 'Todas' : ESTADO_OK_FILAS[v]?.label || `Estado ${v}`;
                    const active = filtroEstado === v || (v === '' && filtroEstado === '');
                    return (
                        <Chip
                            key={String(v)}
                            label={lab}
                            onClick={() => setFiltroEstado(v)}
                            variant={active ? 'filled' : 'outlined'}
                            sx={active ? { bgcolor: COLOR, color: 'white' } : {}}
                            size="small"
                        />
                    );
                })}
                <Tooltip title="Refrescar">
                    <IconButton onClick={cargar} size="small"><RefreshIcon fontSize="small" /></IconButton>
                </Tooltip>
                <Box sx={{ flex: 1 }} />
                {pdfsPendientes.length > 0 && (
                    <Tooltip title="PDFs subidos que no se emparejaron automáticamente con ninguna fila">
                        <Chip
                            icon={<PdfIcon />}
                            label={`${pdfsPendientes.length} PDF${pdfsPendientes.length > 1 ? 's' : ''} sin asignar`}
                            size="small" color="warning"
                            onClick={() => setDialogPdfsOpen(true)}
                            sx={{ cursor: 'pointer' }}
                        />
                    </Tooltip>
                )}
                <Button component="label" size="small" startIcon={<CloudUploadIcon />}
                    sx={{ textTransform: 'none', color: COLOR }}>
                    Subir más PDFs
                    <input type="file" hidden accept=".pdf" multiple onChange={e => subirPdfsExtra(e.target.files)} />
                </Button>
                <Tooltip title="Re-procesar PDFs que ya están en la carpeta staging del lote (reintenta emparejar)">
                    <Button size="small" startIcon={<RestartAltIcon />} onClick={async () => {
                        try {
                            const r = await cargaMasivaReprocesarPdfsStagingJWT(idLote);
                            if (r?.ok) {
                                enqueueSnackbar(`Re-procesado: ${r.matched.length} emparejados, ${r.unmatched.length + r.ambiguous.length} sin match`,
                                    { variant: r.matched.length > 0 ? 'success' : 'info' });
                                cargarPdfsPendientes();
                            } else enqueueSnackbar(r?.mensaje || 'Error', { variant: 'error' });
                        } catch {
                            enqueueSnackbar('Error al re-procesar', { variant: 'error' });
                        }
                    }} sx={{ textTransform: 'none', color: COLOR }}>
                        Re-procesar PDFs
                    </Button>
                </Tooltip>
                {hayPendientes && (
                    <Button size="small" startIcon={<DoneAllIcon />} onClick={bulkCorrectas}
                        sx={{ textTransform: 'none', color: COLOR }}>
                        Marcar pendientes como correctas
                    </Button>
                )}
                {puedeFinalizar && (
                    <Button size="small" variant="contained" startIcon={<PlayArrowIcon />}
                        onClick={() => setConfirmFinalizarOpen(true)}
                        sx={{ bgcolor: COLOR, '&:hover': { bgcolor: '#4a148c' }, textTransform: 'none', fontWeight: 600 }}>
                        Finalizar auditoría
                    </Button>
                )}
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress sx={{ color: COLOR }} />
                </Box>
            ) : filas.length === 0 ? (
                <Alert severity="info">No hay filas. Importá un archivo en el tab "Importar Excel".</Alert>
            ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 340px)' }}>
                    <Table size="small" stickyHeader sx={{
                        '& td, & th': { fontSize: '0.74rem', py: 0.5, px: 0.75 },
                        '& th': { bgcolor: '#ede7f6', fontWeight: 700, color: COLOR, whiteSpace: 'nowrap' },
                    }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 32 }}>#</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell>Demandado</TableCell>
                                <TableCell>DNI / CUIL / CUIT</TableCell>
                                <TableCell>Certificado</TableCell>
                                <TableCell>Imp.</TableCell>
                                <TableCell align="right">Monto</TableCell>
                                <TableCell>Domicilio</TableCell>
                                <TableCell>Exp. Adm</TableCell>
                                <TableCell align="center" sx={{ position: 'sticky', right: 0, bgcolor: '#ede7f6', zIndex: 3 }}>Acc.</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filas.map(f => {
                                const e = ESTADO_OK_FILAS[f.Estado_OK] || { label: `Estado ${f.Estado_OK}`, color: 'default' };
                                const tipoPers = f.Tipo_Persona === 1 ? 'F' : f.Tipo_Persona === 2 ? 'J' : '—';
                                const tipoDoc = f.Tipo_Doc === 1 ? 'DNI' : f.Tipo_Doc === 2 ? 'CUIL' : f.Tipo_Doc === 11 ? 'CUIT' : '';
                                const excluida = f.Estado_OK === 3;
                                // Con expediente del PJ ya es una causa real, y una enviada sin resultado
                                // todavía puede serlo: no se edita ni cambia de estado (motivoFilaEnPJ en
                                // el backend). Una rechazada sí, para reenviarla.
                                const enPJ = !!f.PJ_Expediente_Id || (f.Ciclo_PJ > 0 && !f.PJ_Motivo_Rechazo);
                                const nombreCompleto = [f.Razon_Social, f.Nombre].filter(Boolean).join(', ');
                                const certificado = f.Certificado_Serie
                                    ? `${f.Certificado_Serie}-${f.Certificado_Numero || ''}`
                                    : (f.Certificado_Numero || '—');
                                const domicilioCompleto = [f.Domicilio, f.Calle_Nro].filter(Boolean).join(' ');
                                const localidadCp = [f.Localidad, f.Codigo_Postal].filter(Boolean).join(' · ');
                                return (
                                    <TableRow key={f.Id} hover sx={{ '& td': { whiteSpace: 'nowrap' }, opacity: excluida ? 0.5 : 1 }}>
                                        <TableCell>{f.Fila}</TableCell>
                                        <TableCell><Chip size="small" label={e.label} color={e.color} sx={{ height: 20, fontSize: '0.68rem' }} /></TableCell>
                                        <TableCell sx={{ fontWeight: 500, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            <Tooltip title={`(${f.Tipo_Persona === 1 ? 'Física' : 'Jurídica'}) ${nombreCompleto}`}>
                                                <span>
                                                    <Box component="span" sx={{ display: 'inline-block', minWidth: 14, textAlign: 'center', bgcolor: '#ede7f6', color: COLOR, borderRadius: '3px', px: 0.4, mr: 0.5, fontSize: '0.65rem', fontWeight: 700 }}>{tipoPers}</Box>
                                                    {nombreCompleto || '—'}
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>
                                            <Tooltip title={tipoDoc}><span>{f.Cuit_Dni || '—'}</span></Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>{certificado}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={f.Certificado_Impuesto || '—'}
                                                sx={{ fontFamily: 'monospace', fontSize: '0.63rem', height: 18, bgcolor: '#ede7f6', color: COLOR }} />
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                            {Number(f.Monto_Total || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            <Tooltip title={[domicilioCompleto, localidadCp].filter(Boolean).join(' — ')}>
                                                <span>{domicilioCompleto || localidadCp || '—'}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>{f.Expediente_Ejec || '—'}</TableCell>
                                        <TableCell align="center" sx={{ position: 'sticky', right: 0, bgcolor: 'white', zIndex: 2 }}>
                                            {f.Observaciones && (
                                                (() => {
                                                    const soloAviso = /^\s*\[AVISO\]/.test(f.Observaciones) && !/\|(?!\s*\[AVISO\])/.test(f.Observaciones);
                                                    const color = soloAviso ? '#f57c00' : '#d32f2f';
                                                    return (
                                                        <Tooltip title={f.Observaciones}>
                                                            <IconButton size="small">
                                                                <ErrorIcon fontSize="small" sx={{ color }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    );
                                                })()
                                            )}
                                            {pdfsPendientes.length > 0 && f.Certificado_Numero && (
                                                <Tooltip title="Asignar PDF de certificado">
                                                    <IconButton size="small" onClick={() => setDialogPdfsFila(f)}>
                                                        <PdfIcon fontSize="small" sx={{ color: COLOR }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {enPJ ? (
                                                <Tooltip title={f.PJ_Expediente_Id
                                                    ? `Con expediente del PJ (${f.PJ_Autos_Formateados || f.PJ_Expediente_Id}): ya no se modifica`
                                                    : 'Enviada al PJ, esperando resultado: se sigue desde la pestaña Envío PJ'}>
                                                    <GavelIcon fontSize="small" sx={{ color: COLOR, verticalAlign: 'middle', mx: 0.5 }} />
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title="Editar fila">
                                                    <IconButton size="small" onClick={() => setFilaEditando(f)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {f.Estado_OK !== 1 && f.Estado_OK !== 3 && !enPJ && (
                                                <Tooltip title="Marcar correcta">
                                                    <IconButton size="small" color="success" onClick={() => accionRapida(f.Id, 1, 'Marcada correcta')}>
                                                        <CheckIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {f.Estado_OK !== 3 ? (
                                                !enPJ && lote.Estado < 7 && (
                                                    <Tooltip title="Excluir fila">
                                                        <IconButton size="small" color="error" onClick={() => setFilaExcluir(f)}>
                                                            <BlockIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )
                                            ) : (
                                                lote.Estado < 7 && (
                                                    <Tooltip title="Reincluir">
                                                        <IconButton size="small" onClick={() => accionRapida(f.Id, 2, 'Reincluida (pendiente auditar)')}>
                                                            <UndoIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Dialog editar fila */}
            <DialogEditarFila
                fila={filaEditando}
                idLote={idLote}
                onClose={() => setFilaEditando(null)}
                onSaved={() => { setFilaEditando(null); cargar(); onLoteRefresh?.(); }}
            />

            <DialogExcluirFila
                fila={filaExcluir}
                idLote={idLote}
                onClose={() => setFilaExcluir(null)}
                onExcluida={() => { setFilaExcluir(null); cargar(); onLoteRefresh?.(); }}
            />

            {/* Confirmar finalizar auditoría */}
            <Dialog open={confirmFinalizarOpen} onClose={() => setConfirmFinalizarOpen(false)}
                PaperProps={{ sx: { borderRadius: `${BR}px` } }}>
                <DialogTitle>¿Finalizar auditoría?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Todas las filas deben estar en estado <strong>Correcta</strong> o <strong>Excluida</strong>.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                        <Chip label={`OK: ${lote.Filas_OK || 0}`} color="success" size="small" />
                        <Chip label={`Pendientes: ${lote.Filas_Pendientes || 0}`} color="warning" size="small" />
                        <Chip label={`Defectuosas: ${lote.Filas_Defectuosas || 0}`} color="error" size="small" />
                        <Chip label={`Excluidas: ${lote.Filas_Excluidas || 0}`} size="small" />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmFinalizarOpen(false)}>Cancelar</Button>
                    <Button onClick={finalizarAuditoria} variant="contained"
                        sx={{ bgcolor: COLOR, '&:hover': { bgcolor: '#4a148c' } }}>
                        Finalizar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog: lista global de PDFs sin asignar */}
            <Dialog open={dialogPdfsOpen} onClose={() => setDialogPdfsOpen(false)} fullWidth maxWidth="md"
                PaperProps={{ sx: { borderRadius: `${BR}px` } }}>
                <DialogTitle>PDFs sin asignar ({pdfsPendientes.length})</DialogTitle>
                <DialogContent>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                        Estos archivos se subieron pero ningún número de certificado matcheó con su nombre.
                        Asignalos manualmente desde el ícono PDF de cada fila, o eliminá los que no corresponden.
                    </Typography>
                    {pdfsPendientes.length === 0 ? (
                        <Alert severity="success">No hay PDFs pendientes</Alert>
                    ) : (
                        <Table size="small">
                            <TableBody>
                                {pdfsPendientes.map((p, i) => (
                                    <TableRow key={i}>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{p.nombreArchivo}</TableCell>
                                        <TableCell sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                                            {p.digitos?.length ? `Dígitos: ${p.digitos.join(', ')}` : 'sin dígitos'}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Eliminar este PDF (no corresponde a ninguna fila del lote)">
                                                <IconButton size="small" color="error" onClick={async () => {
                                                    const r = await cargaMasivaEliminarPdfStagingJWT(idLote, p.nombreArchivo);
                                                    if (r?.ok) {
                                                        enqueueSnackbar('PDF eliminado', { variant: 'success' });
                                                        cargarPdfsPendientes();
                                                    } else {
                                                        enqueueSnackbar(r?.mensaje || 'Error al eliminar', { variant: 'error' });
                                                    }
                                                }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogPdfsOpen(false)}>Cerrar</Button>
                </DialogActions>
            </Dialog>

            {/* Dialog: elegir PDF para una fila específica */}
            <Dialog open={!!dialogPdfsFila} onClose={() => setDialogPdfsFila(null)} fullWidth maxWidth="sm"
                PaperProps={{ sx: { borderRadius: `${BR}px` } }}>
                <DialogTitle>
                    Asignar PDF a fila {dialogPdfsFila?.Fila} · Certificado {dialogPdfsFila?.Certificado_Serie}-{dialogPdfsFila?.Certificado_Numero}
                </DialogTitle>
                <DialogContent>
                    {pdfsPendientes.length === 0 ? (
                        <Alert severity="info">No hay PDFs pendientes. Subí archivos con "Subir más PDFs".</Alert>
                    ) : (
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                Elegí el PDF que corresponde a este certificado.
                            </Typography>
                            {pdfsPendientes.map((p, i) => (
                                <Paper key={i} variant="outlined" sx={{
                                    p: 1.5, mb: 1, cursor: 'pointer', borderRadius: `${BR}px`,
                                    '&:hover': { bgcolor: 'rgba(106,27,154,0.05)', borderColor: COLOR },
                                }} onClick={() => asignarPdfAFila(p.nombreArchivo)}>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                                        {p.nombreArchivo}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {p.digitos?.length ? `Dígitos detectados: ${p.digitos.join(', ')}` : 'sin dígitos detectables en el nombre'}
                                    </Typography>
                                </Paper>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogPdfsFila(null)}>Cancelar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

// ============================================================================
// DIALOG EXCLUIR FILA
// ============================================================================
// La justificación es obligatoria, como en el viejo (poder_judicial_json_edita_fila.aspx:79).
function DialogExcluirFila({ fila, idLote, onClose, onExcluida }) {
    const { enqueueSnackbar } = useSnackbar();
    const [justificacion, setJustificacion] = useState('');
    const [excluyendo, setExcluyendo] = useState(false);

    useEffect(() => { setJustificacion(''); }, [fila]);

    const excluir = async () => {
        const texto = justificacion.trim();
        if (!fila || !texto) return;
        setExcluyendo(true);
        try {
            const r = await cargaMasivaAuditarFilaJWT({
                idDetalle: fila.Id, idLote, estadoOk: 3,
                observaciones: `Registro Excluido: ${texto}`,
            });
            if (r?.ok) {
                enqueueSnackbar(r.loteFinalizado ? 'Fila excluida. Lote marcado como "Pasado a SIGIF".' : 'Fila excluida', { variant: 'success' });
                onExcluida();
            } else enqueueSnackbar(r?.mensaje || 'Error al excluir la fila', { variant: 'error' });
        } catch {
            enqueueSnackbar('Error al excluir la fila', { variant: 'error' });
        } finally { setExcluyendo(false); }
    };

    return (
        <Dialog open={!!fila} onClose={() => !excluyendo && onClose()} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: `${BR}px` } }}>
            <DialogTitle>Excluir fila {fila?.Fila}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    {fila?.Razon_Social}{fila?.Nombre && `, ${fila.Nombre}`} · Certificado {fila?.Certificado_Serie}-{fila?.Certificado_Numero}
                </Typography>
                {fila?.PJ_Motivo_Rechazo && (
                    <Typography variant="caption" sx={{ color: '#d32f2f', display: 'block', mb: 2 }}>
                        {fila.PJ_Motivo_Rechazo}
                    </Typography>
                )}
                <Typography variant="body2" sx={{ mb: 2 }}>
                    La fila no se envía al PJ ni pasa a SIGIF. Se puede volver a incluir desde la pestaña Auditoría.
                </Typography>
                <TextField fullWidth multiline minRows={2} size="small" autoFocus required
                    label="Justificación" value={justificacion}
                    onChange={e => setJustificacion(e.target.value)} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={excluyendo}>Cancelar</Button>
                <Button onClick={excluir} variant="contained" color="error"
                    disabled={excluyendo || !justificacion.trim()}>
                    {excluyendo ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Excluir'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ============================================================================
// DIALOG EDITAR FILA
// ============================================================================
function DialogEditarFila({ fila, idLote, onClose, onSaved }) {
    const { enqueueSnackbar } = useSnackbar();
    const [guardando, setGuardando] = useState(false);
    const [form, setForm] = useState({});

    useEffect(() => {
        if (fila) {
            setForm({
                tipoPersona: fila.Tipo_Persona ?? '',
                razonSocial: fila.Razon_Social ?? '',
                nombre: fila.Nombre ?? '',
                sexo: fila.Sexo ?? '',
                tipoDoc: fila.Tipo_Doc ?? '',
                cuitDni: fila.Cuit_Dni ?? '',
                certificadoSerie: fila.Certificado_Serie ?? '',
                certificadoNumero: fila.Certificado_Numero ?? '',
                certificadoImpuesto: fila.Certificado_Impuesto ?? '',
                certificadoCuenta: fila.Certificado_Cuenta ?? '',
                monto: fila.Monto_Total ?? '',
                domicilio: fila.Domicilio ?? '',
                calleNro: fila.Calle_Nro ?? '',
                localidad: fila.Localidad ?? '',
                codigoPostal: fila.Codigo_Postal ?? '',
                expedienteEjec: fila.Expediente_Ejec ?? '',
                expedienteOriginal: fila.Expediente_Original ?? '',
                idMatchingPdf: fila.Id_Matching_PDF ?? '',
            });
        }
    }, [fila]);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const guardar = async () => {
        setGuardando(true);
        try {
            const campos = {
                ...form,
                tipoPersona: form.tipoPersona === '' ? null : Number(form.tipoPersona),
                tipoDoc: form.tipoDoc === '' ? null : Number(form.tipoDoc),
                monto: form.monto === '' ? null : Number(form.monto),
            };
            const r = await cargaMasivaEditarFilaJWT(idLote, fila.Id, campos);
            if (r?.ok) {
                if (r.estadoOk === 1) {
                    enqueueSnackbar('Fila guardada y validada como correcta', { variant: 'success' });
                } else {
                    enqueueSnackbar('Fila guardada, sigue con errores de validación', { variant: 'warning' });
                }
                onSaved?.();
            } else {
                enqueueSnackbar(r?.mensaje || 'Error al guardar', { variant: 'error' });
            }
        } catch {
            enqueueSnackbar('Error al guardar', { variant: 'error' });
        } finally {
            setGuardando(false);
        }
    };

    if (!fila) return null;

    return (
        <Dialog open={!!fila} onClose={onClose} maxWidth="md" fullWidth
            PaperProps={{ sx: { borderRadius: `${BR}px` } }}>
            <DialogTitle sx={{ color: COLOR, fontWeight: 700, pb: 1 }}>
                Editar fila #{fila.Fila}
                {fila.Observaciones && (
                    <Typography variant="caption" sx={{ display: 'block', color: '#d32f2f', fontWeight: 400 }}>
                        Errores actuales: {fila.Observaciones}
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2} sx={{ pt: 1 }}>
                    <Grid item xs={12}><Typography variant="overline" color="text.secondary">Persona</Typography></Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField select fullWidth size="small" label="Tipo"
                            value={form.tipoPersona} onChange={e => set('tipoPersona', e.target.value)}>
                            <MenuItem value={1}>Física</MenuItem>
                            <MenuItem value={2}>Jurídica</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={form.tipoPersona === 2 ? 8 : 5}>
                        <TextField fullWidth size="small"
                            label={form.tipoPersona === 2 ? 'Razón Social' : 'Apellido'}
                            value={form.razonSocial} onChange={e => set('razonSocial', e.target.value)} />
                    </Grid>
                    {form.tipoPersona !== 2 && (
                        <Grid item xs={12} sm={3}>
                            <TextField fullWidth size="small" label="Nombre"
                                value={form.nombre} onChange={e => set('nombre', e.target.value)} />
                        </Grid>
                    )}
                    <Grid item xs={12} sm={1}>
                        <TextField select fullWidth size="small" label="Sexo"
                            value={form.sexo} onChange={e => set('sexo', e.target.value)}>
                            <MenuItem value="">—</MenuItem>
                            <MenuItem value="M">M</MenuItem>
                            <MenuItem value="F">F</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sx={{ mt: 1 }}><Typography variant="overline" color="text.secondary">Documento</Typography></Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField select fullWidth size="small" label="Tipo Doc"
                            value={form.tipoDoc} onChange={e => set('tipoDoc', e.target.value)}>
                            <MenuItem value={1}>DNI</MenuItem>
                            <MenuItem value={2}>CUIL</MenuItem>
                            <MenuItem value={11}>CUIT</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <TextField fullWidth size="small" label="DNI / CUIL / CUIT" placeholder="Sin puntos ni guiones"
                            value={form.cuitDni} onChange={e => set('cuitDni', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth size="small" label="ID Matching PDF"
                            value={form.idMatchingPdf} onChange={e => set('idMatchingPdf', e.target.value)} />
                    </Grid>

                    <Grid item xs={12} sx={{ mt: 1 }}><Typography variant="overline" color="text.secondary">Certificado y Monto</Typography></Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField fullWidth size="small" label="Serie"
                            value={form.certificadoSerie} onChange={e => set('certificadoSerie', e.target.value)} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField fullWidth size="small" label="Certificado"
                            value={form.certificadoNumero} onChange={e => set('certificadoNumero', e.target.value)} />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField fullWidth size="small" label="Impuesto"
                            value={form.certificadoImpuesto}
                            onChange={e => set('certificadoImpuesto', e.target.value.toUpperCase())}
                            inputProps={{ maxLength: 5 }} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField fullWidth size="small" label="Cuenta"
                            value={form.certificadoCuenta} onChange={e => set('certificadoCuenta', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <TextField fullWidth size="small" label="Monto" type="number"
                            value={form.monto} onChange={e => set('monto', e.target.value)} />
                    </Grid>

                    <Grid item xs={12} sx={{ mt: 1 }}><Typography variant="overline" color="text.secondary">Domicilio</Typography></Grid>
                    <Grid item xs={12} sm={5}>
                        <TextField fullWidth size="small" label="Domicilio"
                            value={form.domicilio} onChange={e => set('domicilio', e.target.value)} />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField fullWidth size="small" label="Calle Nro"
                            value={form.calleNro} onChange={e => set('calleNro', e.target.value)} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField fullWidth size="small" label="Localidad"
                            value={form.localidad} onChange={e => set('localidad', e.target.value)} />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField fullWidth size="small" label="CP"
                            value={form.codigoPostal} onChange={e => set('codigoPostal', e.target.value)} />
                    </Grid>

                    <Grid item xs={12} sx={{ mt: 1 }}><Typography variant="overline" color="text.secondary">Expediente</Typography></Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Expediente Ejec"
                            value={form.expedienteEjec} onChange={e => set('expedienteEjec', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Exp. Original"
                            value={form.expedienteOriginal} onChange={e => set('expedienteOriginal', e.target.value)} />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={guardando}>Cancelar</Button>
                <Button onClick={guardar} variant="contained" disabled={guardando}
                    sx={{ bgcolor: COLOR, '&:hover': { bgcolor: '#4a148c' } }}>
                    {guardando ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Guardar y validar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ============================================================================
// TAB ENVÍO PJ
// ============================================================================

// Avance de un envío al PJ. Primero el backend arma el envío (lee los PDF de todas las causas,
// lo más largo); después manda una tanda por POST y la guarda apenas el PJ la recibe: las tandas
// nuevas son los envíos que no estaban al tocar el botón.
function ProgresoEnvioPJ({ progreso, envios, ahora }) {
    const { antes, tandas, causas, desde } = progreso;
    const nuevos = envios.filter(e => !antes.has(e.Id)).reverse(); // en orden de envío
    const hechas = Math.min(nuevos.length, tandas);
    const seg = Math.max(0, Math.round((ahora - desde) / 1000));
    const texto = hechas === 0
        ? `Armando el envío: se leen los PDF de ${causas} causa${causas === 1 ? '' : 's'}…`
        : hechas < tandas
            ? `El PJ recibió ${hechas} de ${tandas} tandas. Mandando la siguiente…`
            : `El PJ recibió ${tandas === 1 ? 'el envío' : `las ${tandas} tandas`}. Terminando…`;
    return (
        <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{texto}</Typography>
                <Typography variant="body2" color="text.secondary">{mmss(seg)}</Typography>
            </Box>
            <LinearProgress variant={hechas === 0 ? 'indeterminate' : 'determinate'} value={(hechas * 100) / tandas}
                sx={{ height: 8, borderRadius: `${BR}px` }} />
            {nuevos.length > 0 && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    Lotes PJ: <strong>{nuevos.map(e => e.Id_Lote_PJ || 'con error').join(', ')}</strong>
                </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                No cierres ni recargues la página hasta que termine.
            </Typography>
        </Box>
    );
}

function TabEnvioPJ({ lote, onLoteRefresh }) {
    const idLote = lote.Id;
    const { enqueueSnackbar } = useSnackbar();
    const [envios, setEnvios] = useState([]);
    const [modoSimulacion, setModoSimulacion] = useState(true);
    const [causasPorEnvio, setCausasPorEnvio] = useState(99);
    const [filas, setFilas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [progresoEnvio, setProgresoEnvio] = useState(null);
    const [ahoraEnvio, setAhoraEnvio] = useState(Date.now());
    const [consultando, setConsultando] = useState(false);
    const [preview, setPreview] = useState(null);
    const [dialogPreview, setDialogPreview] = useState(false);
    const [dialogConfirm, setDialogConfirm] = useState(false);

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const [e, r] = await Promise.all([
                cargaMasivaEnviosPJJWT(idLote),
                cargaMasivaListarDetalleJWT(idLote),
            ]);
            if (e?.ok) {
                setEnvios(e.envios || []);
                setModoSimulacion(!!e.modoSimulacion);
                if (e.causasPorEnvio) setCausasPorEnvio(e.causasPorEnvio);
            }
            if (r?.ok) setFilas((r.registros || []).filter(x => x.Id_Personal_Abogado));
        } finally { setLoading(false); }
    }, [idLote]);

    useEffect(() => { cargar(); }, [cargar]);

    // Mientras manda, relee los envíos cada 2 s para mostrar las tandas que ya llegaron al PJ
    // y el número de lote que tomó cada una.
    useEffect(() => {
        if (!progresoEnvio) return;
        const t = setInterval(async () => {
            setAhoraEnvio(Date.now());
            try {
                const e = await cargaMasivaEnviosPJJWT(idLote);
                if (e?.ok) setEnvios(e.envios || []);
            } catch { /* el próximo intento lo vuelve a leer */ }
        }, 2000);
        return () => clearInterval(t);
    }, [progresoEnvio, idLote]);

    const iniciarProgresoEnvio = (causas) => {
        setProgresoEnvio({
            desde: Date.now(), causas,
            antes: new Set(envios.map(e => e.Id)),
            tandas: Math.max(1, Math.ceil(causas / causasPorEnvio)),
        });
        setAhoraEnvio(Date.now());
    };

    // Todos los lotes PJ que tomó un envío: uno por tanda.
    const textoLotesPJ = (r) => (r.idsControlPJ?.length > 1
        ? `en ${r.idsControlPJ.length} tandas. Lotes PJ: ${r.idsControlPJ.join(', ')}`
        : `Lote PJ: ${r.idLotePJ}`);

    const ultimoEnvio = envios[0] || null;
    const esSimulacion = ultimoEnvio?.Simulado;
    const loteBloqueado = lote.Estado >= 7;
    // Un envío terminó cuando a ninguna fila de su ciclo le falta resultado (expediente o
    // rechazo). El estado del lote es interno del PJ: el 4194 terminó en 7 "Acuse Recibo
    // Generado", sin pasar por 4. Los estados 4 y 5 se siguen tomando como finales.
    // Las filas excluidas no esperan nada del PJ: siguen en la grilla, pero no cuentan.
    const filasActivas = filas.filter(f => f.Estado_OK !== 3);
    const envioTerminado = (e) => e.Estado_Lote_PJ_Id === 4 || e.Estado_Lote_PJ_Id === 5
        || !filasActivas.some(f => f.Ciclo_PJ === e.Ciclo && !f.PJ_Expediente_Id && !f.PJ_Motivo_Rechazo);
    // Un lote puede tener varios envíos: sublotes de 99 causas, envíos de a partes,
    // rectificativas. Mirar solo el último ocultaba los demás.
    const hayEnviosSinTerminar = envios.some(e => e.Id_Lote_PJ && e.Success && !envioTerminado(e));

    // Filtros + búsqueda sobre la grilla
    const [filtroEstado, setFiltroEstado] = useState('todos'); // 'todos'|'expediente'|'rechazadas'|'sinprocesar'|'pasadas'|'listas'
    const [busqueda, setBusqueda] = useState('');

    const stats = {
        totalConAbogado: filas.length,
        conExpediente: filas.filter(f => f.PJ_Expediente_Id).length,
        rechazadas: filasActivas.filter(f => f.PJ_Motivo_Rechazo).length,
        sinProcesar: filasActivas.filter(f => !f.PJ_Expediente_Id && !f.PJ_Motivo_Rechazo).length,
        // Nunca enviadas (Ciclo_PJ = 0): las que toma "Enviar al PJ", como el PJ_Enviado = 0
        // del viejo. Las que no estén listas (firmas, PDFs) las excluye el backend y lo informa.
        pendientesEnvio: filasActivas.filter(f => !(f.Ciclo_PJ > 0) && !f.PJ_Expediente_Id && !f.PJ_Motivo_Rechazo).length,
        // Enviadas y todavía sin resultado: las que justifican volver a consultar.
        enviadasSinResultado: filasActivas.filter(f => f.Ciclo_PJ > 0 && !f.PJ_Expediente_Id && !f.PJ_Motivo_Rechazo).length,
        pasadasSigif: filas.filter(f => f.Pasaje_Estado === 1).length,
        listasParaPasar: filas.filter(f => !!f.PJ_Validada && f.PJ_Expediente_Id && !f.Pasaje_Estado).length,
    };

    const filasFiltradas = useMemo(() => {
        const q = busqueda.trim().toLowerCase();
        return filas.filter(f => {
            // Filtro por estado (toggle desde las cards)
            if (filtroEstado === 'expediente' && !f.PJ_Expediente_Id) return false;
            if (filtroEstado === 'rechazadas' && (!f.PJ_Motivo_Rechazo || f.Estado_OK === 3)) return false;
            if (filtroEstado === 'sinprocesar' && (f.PJ_Expediente_Id || f.PJ_Motivo_Rechazo || f.Estado_OK === 3)) return false;
            if (filtroEstado === 'pasadas' && f.Pasaje_Estado !== 1) return false;
            if (filtroEstado === 'listas' && !(f.PJ_Validada && f.PJ_Expediente_Id && !f.Pasaje_Estado)) return false;
            // Búsqueda libre en demandado, certificado, expediente PJ, juzgado
            if (q) {
                const haystack = [
                    f.Razon_Social, f.Nombre, f.Cuit_Dni,
                    `${f.Certificado_Serie || ''}-${f.Certificado_Numero || ''}`,
                    f.PJ_Autos_Formateados, f.PJ_Motivo_Rechazo,
                    f.Id_Expediente_Creado ? String(f.Id_Expediente_Creado) : '',
                ].filter(Boolean).join(' ').toLowerCase();
                if (!haystack.includes(q)) return false;
            }
            return true;
        });
    }, [filas, filtroEstado, busqueda]);

    const exportarExcel = () => {
        if (filasFiltradas.length === 0) {
            enqueueSnackbar('No hay filas para exportar con los filtros actuales', { variant: 'warning' });
            return;
        }
        const data = filasFiltradas.map(f => ({
            'Fila': f.Fila,
            'Demandado': [f.Razon_Social, f.Nombre].filter(Boolean).join(', '),
            'CUIT/DNI': f.Cuit_Dni || '',
            'Certificado': `${f.Certificado_Serie || ''}-${f.Certificado_Numero || ''}`,
            // Abogado apoderado actual (de la fila). Usamos Abogado_Nombre_Actual
            // (join con Personal) y caemos a Abogado_Nombre (string cacheado) si
            // viene vacío. El usuario pidió que cada causa traiga su abogado.
            'Abogado Apoderado': f.Abogado_Nombre_Actual || f.Abogado_Nombre || '',
            'Matrícula': f.Abogado_Matricula || '',
            'Carta Poder': f.Carta_Poder_Numero
                ? `${lote.Id_Modulo === 1 ? 'A' : 'B'}-${String(f.Carta_Poder_Numero).padStart(6, '0')}/${String(f.Carta_Poder_Ano).padStart(2, '0')}`
                : '',
            'Estado PJ': f.PJ_Motivo_Rechazo ? 'Rechazada'
                : f.PJ_Expediente_Id ? 'Expediente creado'
                : f.Ciclo_PJ > 0 ? 'En proceso' : 'Pendiente envío',
            'Estado de la causa en el PJ': f.PJ_Estado_Causa || '',
            'Ciclo PJ': f.Ciclo_PJ || '',
            'Expediente PJ': f.PJ_Autos_Formateados || '',
            'Id Juzgado PJ': f.PJ_Juzgado_Id || '',
            'Id Expediente SIGIF': f.Id_Expediente_Creado || '',
            'Fecha pasaje SIGIF': f.Pasaje_Fecha ? new Date(f.Pasaje_Fecha).toLocaleString('es-AR') : '',
            'Motivo rechazo': f.PJ_Motivo_Rechazo || '',
            'Error pasaje': f.Pasaje_Error || '',
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Estado PJ');
        const fechaSlug = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(wb, `lote_${idLote}_estado_pj_${fechaSlug}.xlsx`);
    };

    const handlePreview = async () => {
        try {
            const r = await cargaMasivaPreviewJsonPJJWT(idLote);
            if (r?.ok) {
                setPreview(r);
                setDialogPreview(true);
            } else enqueueSnackbar(r?.mensaje || 'Error al armar JSON', { variant: 'error' });
        } catch (err) {
            enqueueSnackbar('Error al armar JSON', { variant: 'error' });
        }
    };

    const handleEnviar = async () => {
        iniciarProgresoEnvio(stats.pendientesEnvio);
        setEnviando(true);
        try {
            const r = await cargaMasivaEnviarAlPJJWT(idLote);
            if (r?.ok) {
                const prefijo = r.simulado ? '[SIMULACIÓN] ' : '';
                // Las que no estaban listas quedan afuera: decir cuántas y el motivo más común.
                const excl = r.excluidas || [];
                let resumenExcl = '';
                if (excl.length) {
                    const porMotivo = {};
                    excl.forEach(x => { porMotivo[x.motivo] = (porMotivo[x.motivo] || 0) + 1; });
                    const [motivo, n] = Object.entries(porMotivo).sort((a, b) => b[1] - a[1])[0];
                    resumenExcl = ` · ${excl.length} quedaron afuera (${n} por "${motivo}"${excl.length > n ? ', y otros motivos' : ''})`;
                }
                enqueueSnackbar(`${prefijo}${r.causasEnviadas} causas enviadas al PJ ${textoLotesPJ(r)}${resumenExcl}`, {
                    variant: 'success', autoHideDuration: 12000,
                });
                setDialogConfirm(false);
                cargar();
            } else {
                enqueueSnackbar(r?.mensaje || 'Error al enviar', { variant: 'error' });
                cargar();
            }
        } finally { setEnviando(false); setProgresoEnvio(null); }
    };

    const handleConsultar = async () => {
        if (!ultimoEnvio) return;
        setConsultando(true);
        try {
            // Sin id de lote PJ: el backend consulta todos los envíos del lote que no
            // terminaron (antes se consultaba solo el último sublote).
            const r = await cargaMasivaConsultarEstadoPJJWT(idLote);
            if (r?.ok) {
                enqueueSnackbar(r.mensaje || r.estadoTxt, { variant: 'info' });
                cargar();
                onLoteRefresh?.();
            } else enqueueSnackbar(r?.mensaje || 'Error', { variant: 'error' });
        } finally { setConsultando(false); }
    };

    const [dialogResetOpen, setDialogResetOpen] = useState(false);
    const handleReset = async () => {
        setDialogResetOpen(false);
        const r = await cargaMasivaResetSimulacionPJJWT(idLote);
        if (r?.ok) {
            enqueueSnackbar('Simulación reseteada', { variant: 'success' });
            cargar();
            onLoteRefresh?.();
        }
    };

    const [dialogRechazoFila, setDialogRechazoFila] = useState(null);
    const [motivoRechazo, setMotivoRechazo] = useState('SIMULACIÓN: error forzado manualmente');
    const handleForzarRechazo = async () => {
        if (!dialogRechazoFila || !motivoRechazo.trim()) return;
        const r = await cargaMasivaForzarRechazoSimuladoJWT(idLote, dialogRechazoFila.Id, motivoRechazo);
        if (r?.ok) {
            enqueueSnackbar(`Fila ${dialogRechazoFila.Fila} marcada como rechazada`, { variant: 'warning' });
            setDialogRechazoFila(null);
            cargar();
        } else {
            enqueueSnackbar(r?.mensaje || 'Error', { variant: 'error' });
        }
    };

    const [reenviando, setReenviando] = useState(false);
    const [dialogReenviarOpen, setDialogReenviarOpen] = useState(false);
    const handleReenviarRechazadas = async () => {
        // El diálogo queda abierto hasta que termine, con el avance (como "Enviar al PJ").
        iniciarProgresoEnvio(stats.rechazadas);
        setReenviando(true);
        try {
            const r = await cargaMasivaReenviarRechazadasPJJWT(idLote);
            if (r?.ok) {
                enqueueSnackbar(`Rectificativa enviada (ciclo ${r.ciclo}): ${r.causasEnviadas} causas ${textoLotesPJ(r)}`,
                    { variant: 'success', autoHideDuration: 12000 });
                cargar();
                onLoteRefresh?.();
            } else {
                enqueueSnackbar(r?.mensaje || 'Error al reenviar', { variant: 'error' });
                cargar();
            }
        } finally { setReenviando(false); setProgresoEnvio(null); setDialogReenviarOpen(false); }
    };

    const [filaEditandoPJ, setFilaEditandoPJ] = useState(null);

    // Excluir una rechazada que no se va a reenviar (certificado duplicado, p. ej.).
    const [filaExcluir, setFilaExcluir] = useState(null);

    const [juzgadosMap, setJuzgadosMap] = useState({});
    useEffect(() => {
        getCombosJWT('juzgados', '', 500)
            .then(r => {
                if (r?.ok && r.items) {
                    const map = {};
                    for (const j of r.items) map[j.id] = j.label || j.juzgado || `Juzgado ${j.id}`;
                    setJuzgadosMap(map);
                }
            })
            .catch(() => {});
    }, []);

    const [dialogPasarSigif, setDialogPasarSigif] = useState(false);
    const [pasandoSigif, setPasandoSigif] = useState(false);
    const [resultadoSigif, setResultadoSigif] = useState(null);
    const handlePasarSigif = async () => {
        setDialogPasarSigif(false);
        setPasandoSigif(true);
        setResultadoSigif(null);
        try {
            const r = await cargaMasivaPasarASigifJWT(idLote);
            if (r?.ok !== false) {
                setResultadoSigif(r);
                enqueueSnackbar(r.mensaje || `${r.exitosas} expedientes creados`,
                    { variant: r.fallidas > 0 ? 'warning' : 'success' });
                cargar(); onLoteRefresh?.();
            } else {
                enqueueSnackbar(r?.mensaje || 'Error al pasar a SIGIF', { variant: 'error' });
            }
        } catch {
            enqueueSnackbar('Error al pasar a SIGIF', { variant: 'error' });
        } finally { setPasandoSigif(false); }
    };

    const descargarJsonPreview = () => {
        if (!preview?.json) return;
        const blob = new Blob([JSON.stringify(preview.json, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `envio-pj-${idLote}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const juzgadoNombre = (id) => juzgadosMap[id] || (id ? `Juzgado ${id}` : '—');

    return (
        <Box>
            {esSimulacion && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <strong>Modo simulación activo.</strong> No se envía nada real al PJ.
                    Cada click en "Consultar estado" avanza 1 paso (En Cola → Validado → Demandas Iniciadas → Expedientes Creados).
                    Al llegar a "Expedientes Creados" el sistema asigna datos ficticios para que puedas probar el pasaje a SIGIF.
                </Alert>
            )}

            {/* Stats */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                {[
                    { key: 'todos', label: 'Con abogado', value: stats.totalConAbogado, color: COLOR },
                    { key: 'expediente', label: 'Con expediente PJ', value: stats.conExpediente, color: '#00897b' },
                    { key: 'rechazadas', label: 'Rechazadas', value: stats.rechazadas, color: '#d32f2f' },
                    { key: 'sinprocesar', label: 'Sin procesar', value: stats.sinProcesar, color: '#f57c00' },
                    { key: 'pasadas', label: 'Pasadas a SIGIF', value: stats.pasadasSigif, color: '#2e7d32' },
                ].map(card => {
                    const activo = filtroEstado === card.key;
                    return (
                        <Grid item xs={6} sm={4} md={2.4} key={card.key}>
                            <Paper variant="outlined"
                                onClick={() => setFiltroEstado(activo ? 'todos' : card.key)}
                                sx={{
                                    p: 1.5, borderLeft: `3px solid ${card.color}`, borderRadius: `${BR}px`,
                                    cursor: 'pointer', transition: 'all .15s',
                                    bgcolor: activo ? `${card.color}11` : 'transparent',
                                    boxShadow: activo ? `0 0 0 2px ${card.color}66` : 'none',
                                    '&:hover': { bgcolor: `${card.color}08` },
                                }}>
                                <Typography variant="caption" color="text.secondary">
                                    {card.label}{activo && <span style={{ color: card.color, marginLeft: 4 }}>(filtrado)</span>}
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>{card.value}</Typography>
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Panel acciones */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: `${BR}px` }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Button size="small" startIcon={<DataObjectIcon />} onClick={handlePreview}
                        sx={{ textTransform: 'none', color: COLOR, borderColor: COLOR }} variant="outlined">
                        Previsualizar JSON
                    </Button>
                    {/* Envío de a partes, como el viejo: disponible siempre que haya filas sin
                        enviar, no solo la primera vez. */}
                    {stats.pendientesEnvio > 0 && !loteBloqueado && (
                        <Button size="small" variant="contained" startIcon={<SendIcon />}
                            onClick={() => setDialogConfirm(true)}
                            sx={{ bgcolor: COLOR, '&:hover': { bgcolor: '#4a148c' }, textTransform: 'none', fontWeight: 600 }}>
                            {ultimoEnvio ? `Enviar ${stats.pendientesEnvio} pendientes al PJ` : 'Enviar al PJ'}
                        </Button>
                    )}
                    {ultimoEnvio && !loteBloqueado && (hayEnviosSinTerminar || stats.enviadasSinResultado > 0) && (
                        <Button size="small" variant="contained" startIcon={<SyncIcon />}
                            onClick={handleConsultar} disabled={consultando}
                            sx={{ bgcolor: COLOR, '&:hover': { bgcolor: '#4a148c' }, textTransform: 'none', fontWeight: 600 }}>
                            {consultando ? <CircularProgress size={16} sx={{ color: 'white' }} />
                                : hayEnviosSinTerminar ? 'Consultar estado' : 'Reintentar asignación'}
                        </Button>
                    )}
                    {ultimoEnvio && !hayEnviosSinTerminar && stats.rechazadas > 0 && !loteBloqueado && (
                        <Button size="small" variant="contained" startIcon={<ReplayIcon />}
                            onClick={() => setDialogReenviarOpen(true)} disabled={reenviando}
                            sx={{ bgcolor: '#c2185b', '&:hover': { bgcolor: '#880e4f' }, textTransform: 'none', fontWeight: 600 }}>
                            {reenviando ? <CircularProgress size={16} sx={{ color: 'white' }} /> : `Reenviar ${stats.rechazadas} ${stats.rechazadas === 1 ? 'rechazada' : 'rechazadas'}`}
                        </Button>
                    )}
                    {stats.listasParaPasar > 0 && !loteBloqueado && (
                        <Button size="small" variant="contained" startIcon={<GavelIcon />}
                            onClick={() => setDialogPasarSigif(true)} disabled={pasandoSigif}
                            sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' }, textTransform: 'none', fontWeight: 600 }}>
                            {pasandoSigif ? <CircularProgress size={16} sx={{ color: 'white' }} /> : `Pasar ${stats.listasParaPasar} a SIGIF`}
                        </Button>
                    )}
                    {esSimulacion && (
                        <Button size="small" startIcon={<RestartAltIcon />} onClick={() => setDialogResetOpen(true)}
                            sx={{ textTransform: 'none', color: '#d32f2f' }}>
                            Resetear simulación
                        </Button>
                    )}
                    <Box sx={{ flex: 1 }} />
                    {/* Todos los envíos, no solo el último: cada tanda toma su número de lote PJ. */}
                    {envios.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                                {envios.length === 1 ? 'Lote PJ:' : `${envios.length} lotes PJ:`}
                            </Typography>
                            {envios.slice().reverse().map(e => {
                                const fecha = parseFechaServer(e.Fecha_Envio);
                                return (
                                    <Tooltip key={e.Id}
                                        title={`Ciclo ${e.Ciclo || 1} · enviado ${fecha ? fecha.toLocaleString('es-AR') : '—'} por ${e.Usuario || '—'}${e.Error_Mensaje ? ` · ${e.Error_Mensaje}` : ''}`}>
                                        <Chip size="small"
                                            label={`${e.Simulado ? '[SIM] ' : ''}${e.Id_Lote_PJ || 'sin número'} · ${e.Estado_Lote_PJ || 'Pendiente'}`}
                                            color={!e.Success || e.Estado_Lote_PJ_Id === 5 ? 'error' : envioTerminado(e) ? 'success' : 'warning'}
                                        />
                                    </Tooltip>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </Paper>

            {/* Buscador + filtros + export */}
            {filas.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
                    <TextField size="small" placeholder="Buscar demandado, certificado, expediente PJ…"
                        value={busqueda} onChange={e => setBusqueda(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Escape') setBusqueda(''); }}
                        sx={{ minWidth: 320, '& .MuiOutlinedInput-root': { borderRadius: `${BR}px` } }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: '#888' }} /></InputAdornment>,
                        }} />
                    {(filtroEstado !== 'todos' || busqueda) && (
                        <Tooltip title="Limpiar filtros">
                            <IconButton size="small" onClick={() => { setFiltroEstado('todos'); setBusqueda(''); }}>
                                <FilterAltOffIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Typography variant="body2" sx={{ color: 'text.secondary', ml: 1 }}>
                        {filasFiltradas.length === filas.length
                            ? `${filas.length} fila${filas.length !== 1 ? 's' : ''}`
                            : `${filasFiltradas.length} de ${filas.length}`}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />}
                        onClick={exportarExcel} disabled={filasFiltradas.length === 0}
                        sx={{ textTransform: 'none', color: COLOR, borderColor: COLOR }}>
                        Exportar Excel
                    </Button>
                </Box>
            )}

            {/* Tabla por fila con datos PJ */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: COLOR }} /></Box>
            ) : filas.length === 0 ? (
                <Alert severity="info">No hay filas con abogado asignado.</Alert>
            ) : filasFiltradas.length === 0 ? (
                <Alert severity="info">No hay filas que coincidan con el filtro/búsqueda actual.</Alert>
            ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 480px)' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow sx={{ '& th': { bgcolor: '#ede7f6', fontWeight: 700, color: COLOR } }}>
                                <TableCell>#</TableCell>
                                <TableCell>Demandado</TableCell>
                                <TableCell>Certificado</TableCell>
                                <TableCell>Carta Poder</TableCell>
                                <TableCell align="center">Estado PJ</TableCell>
                                <TableCell align="center">Ciclo</TableCell>
                                <TableCell>Expediente PJ</TableCell>
                                <TableCell>Juzgado</TableCell>
                                <TableCell align="center">Exp. SIGIF</TableCell>
                                <TableCell>Motivo rechazo</TableCell>
                                {esSimulacion && <TableCell align="center">SIM</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filasFiltradas.map(f => {
                                const prefCp = lote.Id_Modulo === 1 ? 'A' : 'B';
                                const cp = f.Carta_Poder_Numero
                                    ? `${prefCp}-${String(f.Carta_Poder_Numero).padStart(6, '0')}/${String(f.Carta_Poder_Ano).padStart(2, '0')}`
                                    : '—';
                                let estado, color;
                                if (f.Estado_OK === 3) { estado = 'Excluida'; color = 'default'; }
                                else if (f.PJ_Motivo_Rechazo) { estado = 'Rechazada'; color = 'error'; }
                                else if (f.PJ_Expediente_Id) { estado = 'Expediente creado'; color = 'success'; }
                                // Por fila (Ciclo_PJ), no por el último envío del lote: con
                                // envíos de a partes, una fila sin enviar no está "en proceso".
                                // Con el estado que informa el PJ (En cola, Validado, ...) si ya lo trae.
                                else if (f.Ciclo_PJ > 0) { estado = f.PJ_Estado_Causa || 'En proceso'; color = 'warning'; }
                                else { estado = 'Pendiente envío'; color = 'default'; }
                                return (
                                    <TableRow key={f.Id} hover>
                                        <TableCell>{f.Fila}</TableCell>
                                        <TableCell>{f.Razon_Social}{f.Nombre && `, ${f.Nombre}`}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                                            {f.Certificado_Serie}-{f.Certificado_Numero}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{cp}</TableCell>
                                        <TableCell align="center"><Chip size="small" label={estado} color={color} /></TableCell>
                                        <TableCell align="center" sx={{ fontSize: '0.78rem' }}>
                                            {f.Ciclo_PJ > 0 ? f.Ciclo_PJ : '—'}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                            {f.PJ_Autos_Formateados || '—'}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.78rem' }}>{juzgadoNombre(f.PJ_Juzgado_Id)}</TableCell>
                                        <TableCell align="center" sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.78rem' }}>
                                            {f.Id_Expediente_Creado ? (
                                                <Tooltip title={`Abrir expediente Id ${f.Id_Expediente_Creado}${f.PJ_Autos_Formateados ? ` (Autos ${f.PJ_Autos_Formateados})` : ''} en nueva pestaña${f.Pasaje_Fecha ? ` · Pasado ${new Date(f.Pasaje_Fecha).toLocaleString()}` : ''}`}>
                                                    <Chip size="small" clickable
                                                        icon={<OpenInNewIcon fontSize="small" />}
                                                        label={f.PJ_Autos_Formateados || f.Id_Expediente_Creado}
                                                        color="success" variant="outlined"
                                                        onClick={() => window.open(`/expediente/judicial/${f.Id_Expediente_Creado}`, '_blank')}
                                                        sx={{ fontFamily: 'monospace' }} />
                                                </Tooltip>
                                            ) : f.Pasaje_Error ? (
                                                <Tooltip title={f.Pasaje_Error}>
                                                    <Chip size="small" label="error" color="error" variant="outlined" />
                                                </Tooltip>
                                            ) : '—'}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.72rem', maxWidth: 280 }}>
                                            {f.PJ_Motivo_Rechazo && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Tooltip title={f.PJ_Motivo_Rechazo}>
                                                        <Typography variant="caption" sx={{ color: '#d32f2f', flex: 1 }}>
                                                            {f.PJ_Motivo_Rechazo.slice(0, 38)}{f.PJ_Motivo_Rechazo.length > 38 ? '…' : ''}
                                                        </Typography>
                                                    </Tooltip>
                                                    {f.Estado_OK !== 3 && !loteBloqueado && (
                                                        <>
                                                            <Tooltip title="Corregir datos y preparar para reenvío">
                                                                <IconButton size="small" onClick={() => setFilaEditandoPJ(f)}>
                                                                    <EditIcon fontSize="small" sx={{ color: COLOR }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Excluir: no se reenvía y deja de impedir el cierre del lote">
                                                                <IconButton size="small" onClick={() => setFilaExcluir(f)}>
                                                                    <BlockIcon fontSize="small" sx={{ color: '#d32f2f' }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                </Box>
                                            )}
                                        </TableCell>
                                        {esSimulacion && (
                                            <TableCell align="center">
                                                {!f.PJ_Motivo_Rechazo && f.Ciclo_PJ > 0 && (
                                                    <Tooltip title="Forzar rechazo (simulación)">
                                                        <IconButton size="small" onClick={() => { setDialogRechazoFila(f); setMotivoRechazo('SIMULACIÓN: error forzado manualmente'); }}>
                                                            <BlockIcon fontSize="small" sx={{ color: '#d32f2f' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Preview JSON dialog */}
            <Dialog open={dialogPreview} onClose={() => setDialogPreview(false)} maxWidth="lg" fullWidth
                PaperProps={{ sx: { borderRadius: `${BR}px` } }}>
                <DialogTitle sx={{ color: COLOR }}>
                    JSON de envío al Poder Judicial
                    {preview?.modoSimulacion && <Chip size="small" label="SIMULACIÓN" color="info" sx={{ ml: 2 }} />}
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="caption" color="text.secondary">
                        {preview?.json?.causas?.length || 0} causas. Pegalo en Postman o usalo para validar antes de enviar.
                    </Typography>
                    <pre style={{ background: '#263238', color: '#aed581', padding: 12, borderRadius: 4, overflow: 'auto', maxHeight: '60vh', fontSize: '0.72rem' }}>
                        {preview ? JSON.stringify(preview.json, null, 2) : ''}
                    </pre>
                </DialogContent>
                <DialogActions>
                    <Button onClick={descargarJsonPreview}>Descargar JSON</Button>
                    <Button onClick={() => setDialogPreview(false)}>Cerrar</Button>
                </DialogActions>
            </Dialog>

            {/* Confirm enviar dialog */}
            <Dialog open={dialogConfirm} onClose={() => !enviando && setDialogConfirm(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: `${BR}px` } }}>
                <DialogTitle>Enviar lote al Poder Judicial</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Hay <strong>{stats.pendientesEnvio}</strong> causa{stats.pendientesEnvio === 1 ? '' : 's'} sin enviar.
                        Se envían las que estén listas; las que no (falta firma, PDF o certificado) quedan
                        afuera y el resultado indica cuáles y por qué.
                        {stats.pendientesEnvio > causasPorEnvio && (
                            <> Salen en <strong>{Math.ceil(stats.pendientesEnvio / causasPorEnvio)} tandas</strong> de hasta {causasPorEnvio} causa{causasPorEnvio === 1 ? '' : 's'}, cada una con su número de lote PJ.</>
                        )}
                    </Typography>
                    {/* Antes se mostraba "simulación" siempre que el lote no tuviera envíos, sin
                        mirar el modo real del backend: un primer envío real decía ser simulado. */}
                    {modoSimulacion ? (
                        <Alert severity="info">
                            <strong>Modo simulación</strong>: no se envía nada real, se guarda el JSON en disco para auditoría
                            y se simulan las respuestas. Para desactivar, el admin debe poner <code>PJ_MODO_SIMULACION=false</code>
                            en el <code>.env</code> del backend.
                        </Alert>
                    ) : (
                        <Alert severity="warning">
                            <strong>Envío real</strong>: las causas se mandan al Poder Judicial.
                        </Alert>
                    )}
                    {enviando && progresoEnvio && (
                        <ProgresoEnvioPJ progreso={progresoEnvio} envios={envios} ahora={ahoraEnvio} />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogConfirm(false)} disabled={enviando}>Cancelar</Button>
                    <Button onClick={handleEnviar} variant="contained" disabled={enviando}
                        sx={{ bgcolor: COLOR, '&:hover': { bgcolor: '#4a148c' } }}>
                        {enviando ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Enviar al PJ'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm reenviar rechazadas */}
            <Dialog open={dialogReenviarOpen} onClose={() => !reenviando && setDialogReenviarOpen(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: `${BR}px` } }}>
                <DialogTitle>Reenviar causas rechazadas</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Se reenviarán <strong>{stats.rechazadas}</strong> {stats.rechazadas === 1 ? 'causa rechazada' : 'causas rechazadas'} en un nuevo ciclo.
                    </Typography>
                    <Alert severity="info" sx={{ mt: 1 }}>
                        Asegurate de haber corregido los datos de las filas rechazadas antes de reenviar.
                        Podés editarlas desde el ícono de lápiz junto al motivo de rechazo.
                    </Alert>
                    {reenviando && progresoEnvio && (
                        <ProgresoEnvioPJ progreso={progresoEnvio} envios={envios} ahora={ahoraEnvio} />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogReenviarOpen(false)} disabled={reenviando}>Cancelar</Button>
                    <Button onClick={handleReenviarRechazadas} variant="contained" disabled={reenviando}
                        sx={{ bgcolor: '#c2185b', '&:hover': { bgcolor: '#880e4f' } }}>
                        {reenviando ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Reenviar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm reset simulación */}
            <Dialog open={dialogResetOpen} onClose={() => setDialogResetOpen(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: `${BR}px` } }}>
                <DialogTitle>Resetear simulación PJ</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Se borrarán todos los datos simulados del PJ (expedientes, autos, juzgados, rechazos y ciclos)
                        de las filas de este lote. Los envíos simulados también se borrarán. Esta acción no afecta
                        envíos reales.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogResetOpen(false)}>Cancelar</Button>
                    <Button onClick={handleReset} variant="contained" color="error">
                        Resetear
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Forzar rechazo (simulación) */}
            <Dialog open={!!dialogRechazoFila} onClose={() => setDialogRechazoFila(null)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: `${BR}px` } }}>
                <DialogTitle>Forzar rechazo (simulación)</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Fila <strong>{dialogRechazoFila?.Fila}</strong> · {dialogRechazoFila?.Razon_Social}
                    </Typography>
                    <TextField
                        fullWidth autoFocus multiline minRows={2} label="Motivo del rechazo"
                        value={motivoRechazo}
                        onChange={e => setMotivoRechazo(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogRechazoFila(null)}>Cancelar</Button>
                    <Button onClick={handleForzarRechazo} variant="contained" color="error"
                        disabled={!motivoRechazo.trim()}>
                        Rechazar fila
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Editar fila rechazada (reutiliza DialogEditarFila) */}
            <DialogEditarFila
                fila={filaEditandoPJ}
                idLote={idLote}
                onClose={() => setFilaEditandoPJ(null)}
                onSaved={() => { setFilaEditandoPJ(null); cargar(); onLoteRefresh?.(); }}
            />

            {/* Excluir fila rechazada */}
            <DialogExcluirFila
                fila={filaExcluir}
                idLote={idLote}
                onClose={() => setFilaExcluir(null)}
                onExcluida={() => { setFilaExcluir(null); cargar(); onLoteRefresh?.(); }}
            />

            {/* Confirmar pasar a SIGIF */}
            <Dialog open={dialogPasarSigif} onClose={() => !pasandoSigif && setDialogPasarSigif(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: `${BR}px` } }}>
                <DialogTitle>Pasar expedientes a SIGIF</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Se crearán <strong>{stats.listasParaPasar}</strong> expedientes judiciales reales en SIGIF
                        a partir de los datos validados por el PJ.
                    </Typography>
                    <Alert severity="info" sx={{ mt: 1 }}>
                        Cada fila genera: expediente + radicación + certificado + partes (repartición, apoderados, demandado)
                        + contraparte. El proceso es por fila (si una falla, las otras siguen).
                    </Alert>
                    {stats.rechazadas > 0 && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                            Quedan <strong>{stats.rechazadas}</strong> filas rechazadas. El lote no se cerrará hasta que las proceses.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogPasarSigif(false)} disabled={pasandoSigif}>Cancelar</Button>
                    <Button onClick={handlePasarSigif} variant="contained" disabled={pasandoSigif}
                        sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}>
                        {pasandoSigif ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Pasar a SIGIF'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Resultado del pasaje */}
            <Dialog open={!!resultadoSigif} onClose={() => setResultadoSigif(null)} maxWidth="md" fullWidth
                PaperProps={{ sx: { borderRadius: `${BR}px` } }}>
                <DialogTitle>Resultado del pasaje a SIGIF</DialogTitle>
                <DialogContent>
                    <Alert severity={resultadoSigif?.fallidas > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>{resultadoSigif?.exitosas || 0}</strong> expedientes creados · <strong>{resultadoSigif?.fallidas || 0}</strong> errores
                        </Typography>
                        {resultadoSigif?.loteFinalizado && (
                            <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                                Lote marcado como <strong>Pasado a SIGIF</strong>.
                            </Typography>
                        )}
                    </Alert>
                    {resultadoSigif?.errores?.length > 0 && (
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Errores por fila:</Typography>
                            <Table size="small">
                                <TableBody>
                                    {resultadoSigif.errores.map((e, i) => (
                                        <TableRow key={i}>
                                            <TableCell sx={{ width: 60 }}>{e.fila}</TableCell>
                                            <TableCell>{e.razonSocial}</TableCell>
                                            <TableCell sx={{ color: '#d32f2f', fontSize: '0.78rem' }}>{e.error}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResultadoSigif(null)}>Cerrar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

// ============================================================================
// TAB LOG DE AUDITORÍA (operaciones)
// ============================================================================
function TabAuditoriaLog({ idLote }) {
    const [log, setLog] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        cargaMasivaAuditoriaJWT(idLote)
            .then(r => { if (r?.ok) setLog(r.auditoria || []); })
            .finally(() => setLoading(false));
    }, [idLote]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: COLOR }} /></Box>;
    if (log.length === 0) return <Alert severity="info">Sin registros de operaciones todavía.</Alert>;

    return (
        <TableContainer component={Paper} variant="outlined">
            <Table size="small">
                <TableHead sx={{ bgcolor: 'rgba(106,27,154,0.05)' }}>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 700, color: COLOR }}>Fecha</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: COLOR }}>Usuario</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: COLOR }}>Operación</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: COLOR }}>Detalle</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {log.map(r => (
                        <TableRow key={r.Id}>
                            <TableCell>{new Date(r.Fecha).toLocaleString('es-AR')}</TableCell>
                            <TableCell>{r.Usuario}</TableCell>
                            <TableCell><Chip size="small" label={r.Operacion} /></TableCell>
                            <TableCell>{r.Detalle || '—'}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

// ============================================================================
// TAB ABOGADOS (sorteo + manual)
// ============================================================================
function TabAbogados({ lote, onLoteRefresh }) {
    const idLote = lote.Id;
    const { enqueueSnackbar } = useSnackbar();
    const [modo, setModo] = useState('sorteo');
    const [abogados, setAbogados] = useState([]);
    const [loadingAbogados, setLoadingAbogados] = useState(false);
    const [seleccionados, setSeleccionados] = useState(new Set());
    const [busqueda, setBusqueda] = useState('');
    const [sorteando, setSorteando] = useState(false);
    const [filasSinAbogado, setFilasSinAbogado] = useState([]);
    const [asignados, setAsignados] = useState([]);
    const [confirmFinalizar, setConfirmFinalizar] = useState(false);

    const auditada = lote.Estado >= 3;
    const permisos = usePermisosModulo('CARGA MASIVA');
    const puedeAsignar = permisos.has('ASIGNAR ABOGADOS', 'UPDATE');

    const cargarAbogados = useCallback(async () => {
        setLoadingAbogados(true);
        try {
            const r = await cargaMasivaListarAbogadosJWT(busqueda);
            if (r?.ok) setAbogados(r.abogados || []);
        } finally { setLoadingAbogados(false); }
    }, [busqueda]);

    const cargarFilas = useCallback(async () => {
        const r = await cargaMasivaListarDetalleJWT(idLote);
        if (r?.ok) {
            const correctas = (r.registros || []).filter(x => x.Estado_OK === 1 || x.Estado_OK === 4);
            setFilasSinAbogado(correctas.filter(x => !x.Id_Personal_Abogado));
        }
        const r2 = await cargaMasivaAbogadosLoteJWT(idLote);
        if (r2?.ok) setAsignados(r2.abogados || []);
    }, [idLote]);

    useEffect(() => { if (auditada) { cargarAbogados(); cargarFilas(); } }, [auditada, cargarAbogados, cargarFilas]);

    const toggleAbogado = (id) => {
        setSeleccionados(prev => {
            const s = new Set(prev);
            if (s.has(id)) s.delete(id); else s.add(id);
            return s;
        });
    };

    const toggleTodos = () => {
        if (seleccionados.size === abogados.length) setSeleccionados(new Set());
        else setSeleccionados(new Set(abogados.map(a => a.Id)));
    };

    const handleSortear = async () => {
        if (seleccionados.size === 0) {
            enqueueSnackbar('Seleccioná al menos un abogado', { variant: 'warning' });
            return;
        }
        setSorteando(true);
        try {
            const r = await cargaMasivaSortearAbogadosJWT(idLote, Array.from(seleccionados));
            if (r?.ok) {
                enqueueSnackbar(`${r.asignadas} filas repartidas entre ${r.abogadosUsados} abogados`, { variant: 'success' });
                cargarFilas(); onLoteRefresh?.();
            } else enqueueSnackbar(r?.mensaje || 'Error', { variant: 'error' });
        } finally { setSorteando(false); }
    };

    const handleFinalizar = async () => {
        const r = await cargaMasivaFinalizarAsignacionJWT(idLote);
        if (r?.ok) {
            enqueueSnackbar('Asignación finalizada. Lote listo para generar PDFs.', { variant: 'success' });
            setConfirmFinalizar(false);
            onLoteRefresh?.();
        } else enqueueSnackbar(r?.mensaje || 'No se puede finalizar', { variant: 'warning' });
    };

    if (!auditada) {
        return (
            <Alert severity="warning">
                Primero tenés que <strong>finalizar la auditoría</strong> (tab anterior).
                Solo las filas marcadas como <strong>Correctas</strong> pueden recibir abogado.
            </Alert>
        );
    }

    return (
        <Box>
            {permisos.ready && !puedeAsignar && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    No tenés permiso para <strong>asignar abogados</strong>. Solo podés ver la información.
                    Si necesitás hacer cambios, pedile a un director que te habilite el permiso
                    "ASIGNAR ABOGADOS" en el módulo Carga Masiva.
                </Alert>
            )}

            {/* Stats */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip label={`Correctas: ${lote.Filas_OK || 0}`} size="small" color="success" />
                <Chip label={`Con abogado: ${lote.Filas_Con_Abogado || 0}`} size="small" sx={{ bgcolor: COLOR, color: 'white' }} />
                <Chip label={`Sin asignar: ${filasSinAbogado.length}`} size="small" color={filasSinAbogado.length > 0 ? 'warning' : 'default'} />
                <Box sx={{ flex: 1 }} />
                {filasSinAbogado.length === 0 && lote.Estado < 4 && puedeAsignar && (
                    <Button size="small" variant="contained" startIcon={<PlayArrowIcon />}
                        onClick={() => setConfirmFinalizar(true)}
                        sx={{ bgcolor: COLOR, '&:hover': { bgcolor: '#4a148c' }, textTransform: 'none', fontWeight: 600 }}>
                        Finalizar asignación
                    </Button>
                )}
            </Box>

            {/* Switcher de modo */}
            <Tabs value={modo} onChange={(_, v) => setModo(v)} sx={{
                mb: 2, minHeight: 40,
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 40 },
                '& .Mui-selected': { color: `${COLOR} !important` },
                '& .MuiTabs-indicator': { bgcolor: COLOR },
            }}>
                <Tab value="sorteo" label="Sorteo automático" />
                <Tab value="manual" label="Asignación manual" />
                <Tab value="distribucion" label={`Distribución (${asignados.length})`} />
            </Tabs>

            {modo === 'sorteo' && (
                <Box>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Seleccioná los abogados del pool. El sistema repartirá las <strong>{filasSinAbogado.length} filas sin asignar</strong> en forma equitativa (round-robin aleatorio).
                    </Alert>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                        <TextField size="small" placeholder="Buscar por apellido, nombre o matrícula..."
                            value={busqueda} onChange={e => setBusqueda(e.target.value)}
                            sx={{ flex: 1 }} />
                        <Button size="small" onClick={toggleTodos} sx={{ textTransform: 'none', color: COLOR }}>
                            {seleccionados.size === abogados.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
                        </Button>
                    </Box>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, mb: 2 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow sx={{ '& th': { bgcolor: '#ede7f6', fontWeight: 700, color: COLOR } }}>
                                    <TableCell padding="checkbox"></TableCell>
                                    <TableCell>Apellido, Nombre</TableCell>
                                    <TableCell>Matrícula</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loadingAbogados ? (
                                    <TableRow><TableCell colSpan={3} align="center"><CircularProgress size={24} sx={{ color: COLOR }} /></TableCell></TableRow>
                                ) : abogados.length === 0 ? (
                                    <TableRow><TableCell colSpan={3} align="center">No hay abogados</TableCell></TableRow>
                                ) : abogados.map(a => (
                                    <TableRow key={a.Id} hover onClick={() => toggleAbogado(a.Id)}
                                        sx={{ cursor: 'pointer', bgcolor: seleccionados.has(a.Id) ? 'rgba(106,27,154,0.08)' : 'inherit' }}>
                                        <TableCell padding="checkbox">
                                            <Checkbox size="small" checked={seleccionados.has(a.Id)} sx={{ color: COLOR, '&.Mui-checked': { color: COLOR } }} />
                                        </TableCell>
                                        <TableCell>{a.NombreCompleto}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>{a.Matricula}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Chip label={`${seleccionados.size} abogados seleccionados`} sx={{ bgcolor: COLOR, color: 'white' }} />
                        <Button variant="contained" disabled={sorteando || seleccionados.size === 0 || filasSinAbogado.length === 0 || !puedeAsignar}
                            onClick={handleSortear}
                            sx={{ bgcolor: COLOR, '&:hover': { bgcolor: '#4a148c' }, textTransform: 'none', fontWeight: 600 }}>
                            {sorteando ? <CircularProgress size={18} sx={{ color: 'white' }} /> : `Sortear ${filasSinAbogado.length} filas`}
                        </Button>
                    </Box>
                </Box>
            )}

            {modo === 'manual' && (
                <AsignacionManual idLote={idLote} filasSinAbogado={filasSinAbogado} abogados={abogados}
                    onChange={() => { cargarFilas(); onLoteRefresh?.(); }}
                    puedeAsignar={puedeAsignar} />
            )}

            {modo === 'distribucion' && (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ '& th': { bgcolor: '#ede7f6', fontWeight: 700, color: COLOR } }}>
                                <TableCell>Abogado</TableCell>
                                <TableCell>Matrícula</TableCell>
                                <TableCell align="center">Como principal</TableCell>
                                <TableCell align="center">Como co-apoderado</TableCell>
                                <TableCell align="right">Total filas</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {asignados.length === 0 ? (
                                <TableRow><TableCell colSpan={5} align="center">Aún no hay abogados asignados</TableCell></TableRow>
                            ) : asignados.map(a => (
                                <TableRow key={a.Id}>
                                    <TableCell>{a.NombreCompleto}</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>{a.Matricula}</TableCell>
                                    <TableCell align="center">
                                        {a.ComoPrincipal > 0 ? <Chip size="small" label={a.ComoPrincipal} sx={{ bgcolor: COLOR, color: 'white' }} /> : '—'}
                                    </TableCell>
                                    <TableCell align="center">
                                        {a.ComoCoApoderado > 0 ? <Chip size="small" label={a.ComoCoApoderado} variant="outlined" sx={{ borderColor: COLOR, color: COLOR }} /> : '—'}
                                    </TableCell>
                                    <TableCell align="right"><strong>{a.Cantidad}</strong></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={confirmFinalizar} onClose={() => setConfirmFinalizar(false)}
                PaperProps={{ sx: { borderRadius: `${BR}px` } }}>
                <DialogTitle>¿Finalizar asignación?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Todas las filas correctas tienen abogado. El lote pasará al estado
                        <strong> "Abogados asignados"</strong> y estará listo para generar PDFs (Fase 3).
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmFinalizar(false)}>Cancelar</Button>
                    <Button onClick={handleFinalizar} variant="contained"
                        sx={{ bgcolor: COLOR, '&:hover': { bgcolor: '#4a148c' } }}>
                        Finalizar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

function AsignacionManual({ idLote, filasSinAbogado, abogados, onChange, puedeAsignar = true }) {
    const { enqueueSnackbar } = useSnackbar();
    const [filas, setFilas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [coaposCount, setCoaposCount] = useState({}); // Id_Detalle → count
    const [dialogCoapos, setDialogCoapos] = useState(null);
    // Lista completa de abogados, independiente del filtro de búsqueda del sorteo.
    // Sino el dropdown queda con 5 options cuando había búsqueda activa y los
    // abogados ya asignados que no matchean desaparecen visualmente.
    const [abogadosFull, setAbogadosFull] = useState([]);

    useEffect(() => {
        let cancelado = false;
        cargaMasivaListarAbogadosJWT('').then(r => {
            if (cancelado) return;
            if (r?.ok) setAbogadosFull(r.abogados || []);
        }).catch(() => {});
        return () => { cancelado = true; };
    }, []);

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await cargaMasivaListarDetalleJWT(idLote);
            if (r?.ok) {
                const fs = (r.registros || []).filter(x => x.Estado_OK === 1 || x.Estado_OK === 4);
                setFilas(fs);
                // Cargar count de co-apoderados en paralelo
                const counts = {};
                await Promise.all(fs.map(async (f) => {
                    if (f.Id_Personal_Abogado) {
                        const rp = await cargaMasivaApoderadosFilaJWT(f.Id);
                        if (rp?.ok) {
                            counts[f.Id] = (rp.apoderados || []).filter(a => a.Orden > 1).length;
                        }
                    }
                }));
                setCoaposCount(counts);
            }
        } finally { setLoading(false); }
    }, [idLote]);

    useEffect(() => { cargar(); }, [cargar]);

    const asignar = async (idDetalle, idAbogado) => {
        if (!idAbogado) {
            const r = await cargaMasivaDesasignarAbogadoJWT(idLote, idDetalle);
            if (r?.ok) { enqueueSnackbar('Abogado removido', { variant: 'success' }); cargar(); onChange?.(); }
            return;
        }
        const r = await cargaMasivaAsignarAbogadoManualJWT(idLote, idDetalle, idAbogado);
        if (r?.ok) { enqueueSnackbar('Abogado asignado', { variant: 'success' }); cargar(); onChange?.(); }
        else enqueueSnackbar(r?.mensaje || 'Error', { variant: 'error' });
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: COLOR }} /></Box>;
    if (filas.length === 0) return <Alert severity="info">No hay filas correctas para asignar.</Alert>;

    return (
        <>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 430px)' }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow sx={{ '& th': { bgcolor: '#ede7f6', fontWeight: 700, color: COLOR } }}>
                            <TableCell>#</TableCell>
                            <TableCell>Apellido/Razón</TableCell>
                            <TableCell>CUIL/DNI</TableCell>
                            <TableCell>Certificado</TableCell>
                            <TableCell align="right">Monto</TableCell>
                            <TableCell sx={{ minWidth: 280 }}>Abogado principal</TableCell>
                            <TableCell align="center">Co-apoderados</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filas.map(f => {
                            const countCoapos = coaposCount[f.Id] || 0;
                            const tienePrincipal = !!f.Id_Personal_Abogado;
                            return (
                                <TableRow key={f.Id} hover>
                                    <TableCell>{f.Fila}</TableCell>
                                    <TableCell>{f.Razon_Social} {f.Nombre && `, ${f.Nombre}`}</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>{f.Cuit_Dni}</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>{f.Certificado_Serie}-{f.Certificado_Numero}</TableCell>
                                    <TableCell align="right">{Number(f.Monto_Total || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</TableCell>
                                    <TableCell>
                                        <TextField
                                            select size="small" fullWidth
                                            disabled={!puedeAsignar}
                                            value={f.Id_Personal_Abogado ? String(f.Id_Personal_Abogado) : ''}
                                            onChange={e => asignar(f.Id, e.target.value ? parseInt(e.target.value) : null)}
                                        >
                                            <MenuItem value="">— Sin asignar —</MenuItem>
                                            {abogadosFull.map(a => (
                                                <MenuItem key={a.Id} value={String(a.Id)}>{a.NombreCompleto} ({a.Matricula})</MenuItem>
                                            ))}
                                            {/* Fallback: si el abogado asignado no está en la lista (ej. inactivo),
                                                lo agregamos como opción para que se vea seleccionado */}
                                            {f.Id_Personal_Abogado && !abogadosFull.some(a => String(a.Id) === String(f.Id_Personal_Abogado)) && (
                                                <MenuItem value={String(f.Id_Personal_Abogado)}>
                                                    Id {f.Id_Personal_Abogado} (no listado)
                                                </MenuItem>
                                            )}
                                        </TextField>
                                    </TableCell>
                                    <TableCell align="center">
                                        {tienePrincipal ? (
                                            <Tooltip title={!puedeAsignar ? 'Sin permiso para asignar' : (countCoapos > 0 ? `${countCoapos} co-apoderado(s)` : 'Agregar co-apoderados')}>
                                                <span>
                                                    <Button
                                                        size="small" disabled={!puedeAsignar}
                                                        startIcon={<GroupAddIcon fontSize="small" />}
                                                        onClick={() => setDialogCoapos(f)}
                                                        sx={{ textTransform: 'none', color: countCoapos > 0 ? COLOR : 'text.secondary' }}
                                                    >
                                                        {countCoapos > 0 ? `+${countCoapos}` : 'Agregar'}
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        ) : (
                                            <Typography variant="caption" color="text.disabled">Asigná principal primero</Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {dialogCoapos && (
                <DialogGestionarCoapoderados
                    idLote={idLote}
                    fila={dialogCoapos}
                    abogados={abogados}
                    onClose={() => setDialogCoapos(null)}
                    onChange={() => { cargar(); onChange?.(); }}
                />
            )}
        </>
    );
}

// ============================================================================
// Dialog gestión de co-apoderados de una fila
// ============================================================================
function DialogGestionarCoapoderados({ idLote, fila, abogados, onClose, onChange }) {
    const { enqueueSnackbar } = useSnackbar();
    const [apoderados, setApoderados] = useState([]);
    const [loading, setLoading] = useState(false);
    const [idAbogadoAAgregar, setIdAbogadoAAgregar] = useState('');
    const [guardando, setGuardando] = useState(false);

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await cargaMasivaApoderadosFilaJWT(fila.Id);
            if (r?.ok) setApoderados(r.apoderados || []);
        } finally { setLoading(false); }
    }, [fila.Id]);

    useEffect(() => { cargar(); }, [cargar]);

    const agregar = async () => {
        if (!idAbogadoAAgregar) return;
        setGuardando(true);
        try {
            const r = await cargaMasivaAgregarCoApoderadoJWT(idLote, fila.Id, parseInt(idAbogadoAAgregar));
            if (r?.ok) {
                enqueueSnackbar('Co-apoderado agregado', { variant: 'success' });
                setIdAbogadoAAgregar('');
                cargar(); onChange?.();
            } else enqueueSnackbar(r?.mensaje || 'Error', { variant: 'error' });
        } finally { setGuardando(false); }
    };

    const quitar = async (idPersonal, orden) => {
        if (orden === 1) {
            enqueueSnackbar('El principal se quita desde la columna Abogado principal', { variant: 'warning' });
            return;
        }
        const r = await cargaMasivaQuitarCoApoderadoJWT(idLote, fila.Id, idPersonal);
        if (r?.ok) { enqueueSnackbar('Co-apoderado removido', { variant: 'success' }); cargar(); onChange?.(); }
    };

    const idsYaApoderados = new Set(apoderados.map(a => String(a.Id_Personal)));
    const abogadosDisponibles = abogados.filter(a => !idsYaApoderados.has(String(a.Id)));

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: `${BR}px` } }}>
            <DialogTitle sx={{ color: COLOR, fontWeight: 700 }}>
                Co-apoderados — Fila #{fila.Fila}
                <Typography variant="caption" display="block" color="text.secondary">
                    {fila.Razon_Social} {fila.Nombre && `, ${fila.Nombre}`}
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress sx={{ color: COLOR }} /></Box>
                ) : (
                    <>
                        <Typography variant="overline" color="text.secondary">Apoderados actuales</Typography>
                        <Table size="small" sx={{ mb: 2 }}>
                            <TableBody>
                                {apoderados.map(a => (
                                    <TableRow key={a.Id}>
                                        <TableCell sx={{ width: 80 }}>
                                            <Chip size="small" label={a.Orden === 1 ? 'Principal' : `Co-apo #${a.Orden}`}
                                                sx={a.Orden === 1 ? { bgcolor: COLOR, color: 'white' } : {}} />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{a.Nombre}</Typography>
                                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>Mat. {a.Matricula}</Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ width: 60 }}>
                                            {a.Orden > 1 && (
                                                <Tooltip title="Quitar co-apoderado">
                                                    <IconButton size="small" color="error" onClick={() => quitar(a.Id_Personal, a.Orden)}>
                                                        <PersonRemoveIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        <Typography variant="overline" color="text.secondary">Agregar co-apoderado</Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <TextField
                                select size="small" fullWidth label="Abogado"
                                value={idAbogadoAAgregar}
                                onChange={e => setIdAbogadoAAgregar(e.target.value)}
                            >
                                <MenuItem value="">— Seleccionar —</MenuItem>
                                {abogadosDisponibles.map(a => (
                                    <MenuItem key={a.Id} value={a.Id}>{a.NombreCompleto} (Mat. {a.Matricula})</MenuItem>
                                ))}
                            </TextField>
                            <Button
                                variant="contained" disabled={!idAbogadoAAgregar || guardando}
                                startIcon={<AddIcon />}
                                onClick={agregar}
                                sx={{ bgcolor: COLOR, '&:hover': { bgcolor: '#4a148c' }, textTransform: 'none' }}>
                                Agregar
                            </Button>
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cerrar</Button>
            </DialogActions>
        </Dialog>
    );
}

// ============================================================================
// TAB DOCUMENTOS (generación de PDFs)
// ============================================================================
// Avance de "Generar ahora". El backend graba la ruta de cada PDF apenas lo genera, así que
// lo generado es lo que creció cada contador desde que se tocó el botón.
const ETIQUETAS_DOC = { CARTA_PODER: 'Cartas poder', DEMANDA: 'Demandas', RECIBO: 'Recibos' };
const mmss = (seg) => `${Math.floor(seg / 60)}:${String(seg % 60).padStart(2, '0')}`;

function ProgresoGeneracion({ progreso, actual, ahora }) {
    const { tipos, inicio, meta, desde } = progreso;
    // Sin "Solo faltantes" se regenera lo que ya existe: los contadores no se mueven.
    const medible = !!meta;
    const hechosDe = (t) => Math.min(meta[t], Math.max(0, actual[t] - inicio[t]));
    const total = medible ? tipos.reduce((s, t) => s + meta[t], 0) : 0;
    const hechos = medible ? tipos.reduce((s, t) => s + hechosDe(t), 0) : 0;
    const pct = total ? Math.round((hechos * 100) / total) : 0;
    const seg = Math.max(0, Math.round((ahora - desde) / 1000));
    const restante = hechos >= 3 && hechos < total ? Math.round((seg / hechos) * (total - hechos)) : null;
    return (
        <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {medible
                        ? tipos.map(t => `${ETIQUETAS_DOC[t]}: ${hechosDe(t)} de ${meta[t]}`).join(' · ')
                        : `Regenerando ${tipos.map(t => ETIQUETAS_DOC[t].toLowerCase()).join(', ')}…`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {medible && total > 0 && `${pct}% · `}{mmss(seg)}{restante != null && ` · faltan ~${mmss(restante)}`}
                </Typography>
            </Box>
            <LinearProgress variant={total ? 'determinate' : 'indeterminate'} value={pct} sx={{ height: 8, borderRadius: `${BR}px` }} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Se generan de a uno. No cambies de pestaña ni recargues la página hasta que termine.
            </Typography>
        </Box>
    );
}

function TabDocumentos({ lote, onLoteRefresh }) {
    const idLote = lote.Id;
    const esTrib = lote.Id_Modulo === 1;
    const { enqueueSnackbar } = useSnackbar();
    const [filas, setFilas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [generando, setGenerando] = useState(false);
    const [progreso, setProgreso] = useState(null);
    const [ahora, setAhora] = useState(Date.now());
    const [soloFaltantes, setSoloFaltantes] = useState(true);
    const [tiposSel, setTiposSel] = useState({ CARTA_PODER: true, DEMANDA: true, RECIBO: true });
    const [ultimoResultado, setUltimoResultado] = useState(null);
    const [previewState, setPreviewState] = useState({ open: false, blob: null, titulo: '', filename: '' });
    const [resumenFirma, setResumenFirma] = useState({ total: 0, sinEnviar: 0, enFirma: 0, firmadas: 0 });
    const [enviandoFirma, setEnviandoFirma] = useState(false);
    const [sincronizando, setSincronizando] = useState(false);
    const [resumenFirmaDem, setResumenFirmaDem] = useState({ total: 0, sinEnviar: 0, enFirma: 0, firmadas: 0 });
    const [dialogFaltantes, setDialogFaltantes] = useState(null); // { tipo, filas }
    const [enviandoFirmaDem, setEnviandoFirmaDem] = useState(false);
    const [sincronizandoDem, setSincronizandoDem] = useState(false);

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await cargaMasivaListarDetalleJWT(idLote);
            if (r?.ok) setFilas((r.registros || []).filter(x => x.Id_Personal_Abogado && x.Estado_OK !== 3));
        } finally { setLoading(false); }
    }, [idLote]);

    useEffect(() => { cargar(); }, [cargar]);

    // Mientras genera, relee las filas cada 3 s (sin el spinner de la tabla) para que los
    // contadores y la barra muestren el avance real.
    useEffect(() => {
        if (!generando) return;
        const t = setInterval(async () => {
            setAhora(Date.now());
            try {
                const r = await cargaMasivaListarDetalleJWT(idLote);
                if (r?.ok) setFilas((r.registros || []).filter(x => x.Id_Personal_Abogado && x.Estado_OK !== 3));
            } catch { /* el próximo intento lo vuelve a leer */ }
        }, 3000);
        return () => clearInterval(t);
    }, [generando, idLote]);

    const cargarResumenFirma = useCallback(async () => {
        try {
            const r = await cargaMasivaResumenFirmaJWT(idLote);
            if (r?.ok && r.resumen) setResumenFirma(r.resumen);
        } catch {}
    }, [idLote]);

    useEffect(() => { cargarResumenFirma(); }, [cargarResumenFirma]);

    const cargarResumenFirmaDem = useCallback(async () => {
        try {
            const r = await cargaMasivaResumenFirmaDemandaJWT(idLote);
            if (r?.ok && r.resumen) setResumenFirmaDem(r.resumen);
        } catch {}
    }, [idLote]);

    useEffect(() => { cargarResumenFirmaDem(); }, [cargarResumenFirmaDem]);

    // Auto-refresh resumen cada 15s si hay cartas en firma
    useEffect(() => {
        if (resumenFirma.enFirma > 0 || resumenFirmaDem.enFirma > 0) {
            const t = setInterval(() => { cargarResumenFirma(); cargarResumenFirmaDem(); }, 15000);
            return () => clearInterval(t);
        }
    }, [resumenFirma.enFirma, resumenFirmaDem.enFirma, cargarResumenFirma, cargarResumenFirmaDem]);

    // Auto-sincronización REAL cada 60s (si hay firmas en circulación) — verifica
    // contra Panel_Firma_Auditoria si se firmaron y avanza estados sin que el
    // usuario tenga que apretar "Sincronizar". Silencioso (sin snackbar).
    useEffect(() => {
        if (resumenFirma.enFirma === 0 && resumenFirmaDem.enFirma === 0) return;
        let cancelado = false;
        const sync = async () => {
            try {
                const tasks = [];
                if (resumenFirma.enFirma > 0) tasks.push(cargaMasivaSincronizarFirmasJWT(idLote));
                if (resumenFirmaDem.enFirma > 0) tasks.push(cargaMasivaSincronizarFirmasDemandaJWT(idLote));
                const results = await Promise.all(tasks);
                if (cancelado) return;
                const hayCambios = results.some(r => (r?.firmadas > 0) || (r?.avanzadas > 0) || (r?.completas > 0));
                if (hayCambios) {
                    cargarResumenFirma();
                    cargarResumenFirmaDem();
                    cargar();
                    onLoteRefresh?.();
                }
            } catch { /* silencioso */ }
        };
        const t = setInterval(sync, 60000);
        return () => { cancelado = true; clearInterval(t); };
    }, [resumenFirma.enFirma, resumenFirmaDem.enFirma, idLote, cargar, cargarResumenFirma, cargarResumenFirmaDem, onLoteRefresh]);

    const handleEnviarAFirma = async () => {
        setEnviandoFirma(true);
        try {
            const r = await cargaMasivaEnviarAFirmaJWT(idLote);
            if (r?.ok) {
                enqueueSnackbar(`${r.enviadas} cartas poder enviadas al Panel de Firma`, { variant: 'success' });
                cargarResumenFirma();
                cargar();
            } else enqueueSnackbar(r?.mensaje || 'Error', { variant: 'error' });
        } catch {
            enqueueSnackbar('Error al enviar a firma', { variant: 'error' });
        } finally { setEnviandoFirma(false); }
    };

    const handleSincronizar = async () => {
        setSincronizando(true);
        try {
            const r = await cargaMasivaSincronizarFirmasJWT(idLote);
            if (r?.ok) {
                const msg = r.firmadas > 0
                    ? `${r.firmadas} nueva(s) firmada(s). Pendientes: ${r.pendientes}`
                    : `Sin cambios. Pendientes: ${r.pendientes}`;
                enqueueSnackbar(msg, { variant: r.firmadas > 0 ? 'success' : 'info' });
                cargarResumenFirma();
                cargar();
                onLoteRefresh?.();
            }
        } finally { setSincronizando(false); }
    };

    const handleEnviarDemandasAFirma = async () => {
        setEnviandoFirmaDem(true);
        try {
            const r = await cargaMasivaEnviarDemandasAFirmaJWT(idLote);
            if (r?.ok) {
                enqueueSnackbar(`${r.enviadas} demandas enviadas al primer apoderado`, { variant: 'success' });
                cargarResumenFirmaDem();
                cargar();
            } else enqueueSnackbar(r?.mensaje || 'Error', { variant: 'error' });
        } finally { setEnviandoFirmaDem(false); }
    };

    const handleSincronizarDem = async () => {
        setSincronizandoDem(true);
        try {
            const r = await cargaMasivaSincronizarFirmasDemandaJWT(idLote);
            if (r?.ok) {
                const avanzadas = r.avanzadas || 0;
                const completas = r.completas || 0;
                const msg = (avanzadas > 0 || completas > 0)
                    ? `${avanzadas} avanzaron al siguiente apoderado, ${completas} completamente firmadas`
                    : 'Sin cambios';
                enqueueSnackbar(msg, { variant: (avanzadas > 0 || completas > 0) ? 'success' : 'info' });
                cargarResumenFirmaDem();
                cargar();
            }
        } finally { setSincronizandoDem(false); }
    };

    const puedeGenerar = lote.Estado >= 4;
    const totalConAbogado = filas.length;
    const sinCartaPoder = filas.filter(f => !f.Ruta_PDF_Carta_Poder);
    const sinDemanda = filas.filter(f => !f.Ruta_PDF_Demanda);
    const sinRecibo = filas.filter(f => !f.Ruta_PDF_Recibo);
    const conCartaPoder = totalConAbogado - sinCartaPoder.length;
    const conDemanda = totalConAbogado - sinDemanda.length;
    const conRecibo = totalConAbogado - sinRecibo.length;

    const handleGenerar = async () => {
        const tipos = Object.entries(tiposSel).filter(([, v]) => v).map(([k]) => k);
        if (tipos.length === 0) {
            enqueueSnackbar('Seleccioná al menos un tipo de documento', { variant: 'warning' });
            return;
        }
        const inicio = { CARTA_PODER: conCartaPoder, DEMANDA: conDemanda, RECIBO: conRecibo };
        const faltan = { CARTA_PODER: sinCartaPoder.length, DEMANDA: sinDemanda.length, RECIBO: sinRecibo.length };
        setProgreso({ tipos, inicio, meta: soloFaltantes ? faltan : null, desde: Date.now() });
        setAhora(Date.now());
        setGenerando(true);
        setUltimoResultado(null);
        try {
            const r = await cargaMasivaGenerarDocumentosJWT(idLote, tipos, soloFaltantes);
            if (r?.ok) {
                setUltimoResultado(r);
                const total = (r.cartaPoder || 0) + (r.demanda || 0) + (r.recibo || 0);
                const variant = r.errores?.length > 0 ? 'warning' : 'success';
                enqueueSnackbar(`Generados ${total} PDFs. ${r.errores?.length || 0} errores.`, { variant });
                cargar();
                cargarResumenFirma();
                cargarResumenFirmaDem();
                onLoteRefresh?.();
            } else {
                enqueueSnackbar(r?.mensaje || 'Error al generar', { variant: 'error' });
            }
        } catch {
            enqueueSnackbar('Error al generar PDFs', { variant: 'error' });
        } finally {
            setGenerando(false);
            setProgreso(null);
        }
    };

    const descargar = async (idDetalle, tipo, fila) => {
        try {
            const blob = await cargaMasivaDescargarPDFBlob(idDetalle, tipo);
            const labels = { CARTA_PODER: 'Carta Poder', DEMANDA: 'Demanda', RECIBO: 'Recibo' };
            const filename = `${tipo}_${lote.Id}_fila${fila}.pdf`;
            setPreviewState({
                open: true,
                blob,
                titulo: `${labels[tipo]} — Lote ${lote.Id} — Fila ${fila}`,
                filename,
            });
        } catch {
            enqueueSnackbar('Error al descargar PDF', { variant: 'error' });
        }
    };

    const cerrarPreview = () => setPreviewState(s => ({ ...s, open: false, blob: null }));

    // Exporta a Excel el listado de causas con el estado de su documentación
    // (Carta Poder / Demanda / Recibo) para control. Pedido del usuario.
    const exportarExcelDocumentos = () => {
        if (filas.length === 0) {
            enqueueSnackbar('No hay causas para exportar', { variant: 'warning' });
            return;
        }
        const data = filas.map(f => ({
            'Fila': f.Fila,
            'Demandado': [f.Razon_Social, f.Nombre].filter(Boolean).join(', '),
            'CUIT/DNI': f.Cuit_Dni || '',
            'Certificado': `${f.Certificado_Serie || ''}-${f.Certificado_Numero || ''}`,
            'Abogado Apoderado': f.Abogado_Nombre_Actual || f.Abogado_Nombre || '',
            'Matrícula': f.Abogado_Matricula || '',
            'Nº Carta Poder': f.Carta_Poder_Numero
                ? `${esTrib ? 'A' : 'B'}-${String(f.Carta_Poder_Numero).padStart(6, '0')}/${String(f.Carta_Poder_Ano).padStart(2, '0')}`
                : '',
            'Carta Poder': f.Ruta_PDF_Carta_Poder ? 'Sí' : 'No',
            'Demanda': f.Ruta_PDF_Demanda ? 'Sí' : 'No',
            'Recibo': f.Ruta_PDF_Recibo ? 'Sí' : 'No',
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        ws['!cols'] = [{ wch: 6 }, { wch: 32 }, { wch: 16 }, { wch: 16 }, { wch: 28 }, { wch: 12 }, { wch: 16 }, { wch: 11 }, { wch: 9 }, { wch: 8 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Documentacion');
        const fechaSlug = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(wb, `lote_${idLote}_documentacion_${fechaSlug}.xlsx`);
    };

    if (!puedeGenerar) {
        return (
            <Alert severity="warning">
                Primero tenés que <strong>finalizar la asignación de abogados</strong> (tab anterior).
            </Alert>
        );
    }

    return (
        <Box>
            {/* Stats */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} md={3}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderLeft: `3px solid ${COLOR}`, borderRadius: `${BR}px` }}>
                        <Typography variant="caption" color="text.secondary">Con abogado</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>{totalConAbogado}</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                    <Paper variant="outlined"
                        onClick={sinCartaPoder.length > 0 ? () => setDialogFaltantes({ tipo: 'Carta Poder', filas: sinCartaPoder }) : undefined}
                        sx={{ p: 1.5, borderLeft: '3px solid #5e35b1', borderRadius: `${BR}px`,
                            cursor: sinCartaPoder.length > 0 ? 'pointer' : 'default',
                            '&:hover': sinCartaPoder.length > 0 ? { boxShadow: 2, bgcolor: 'rgba(94,53,177,0.04)' } : {},
                        }}>
                        <Typography variant="caption" color="text.secondary">Cartas Poder {sinCartaPoder.length > 0 && '(ver faltantes)'}</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>{conCartaPoder}<Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.6 }}>/ {totalConAbogado}</Typography></Typography>
                    </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                    <Paper variant="outlined"
                        onClick={sinDemanda.length > 0 ? () => setDialogFaltantes({ tipo: 'Demanda', filas: sinDemanda }) : undefined}
                        sx={{ p: 1.5, borderLeft: '3px solid #c2185b', borderRadius: `${BR}px`,
                            cursor: sinDemanda.length > 0 ? 'pointer' : 'default',
                            '&:hover': sinDemanda.length > 0 ? { boxShadow: 2, bgcolor: 'rgba(194,24,91,0.04)' } : {},
                        }}>
                        <Typography variant="caption" color="text.secondary">Demandas {sinDemanda.length > 0 && '(ver faltantes)'}</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>{conDemanda}<Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.6 }}>/ {totalConAbogado}</Typography></Typography>
                    </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                    <Paper variant="outlined"
                        onClick={sinRecibo.length > 0 ? () => setDialogFaltantes({ tipo: 'Recibo', filas: sinRecibo }) : undefined}
                        sx={{ p: 1.5, borderLeft: '3px solid #00897b', borderRadius: `${BR}px`,
                            cursor: sinRecibo.length > 0 ? 'pointer' : 'default',
                            '&:hover': sinRecibo.length > 0 ? { boxShadow: 2, bgcolor: 'rgba(0,137,123,0.04)' } : {},
                        }}>
                        <Typography variant="caption" color="text.secondary">Recibos {sinRecibo.length > 0 && '(ver faltantes)'}</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>{conRecibo}<Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.6 }}>/ {totalConAbogado}</Typography></Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Panel de generación */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: `${BR}px` }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Generar documentos</Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControlLabel control={<Checkbox checked={tiposSel.CARTA_PODER} onChange={e => setTiposSel(t => ({ ...t, CARTA_PODER: e.target.checked }))} />} label="Carta Poder" />
                    <FormControlLabel
                        control={<Checkbox checked={tiposSel.DEMANDA} onChange={e => setTiposSel(t => ({ ...t, DEMANDA: e.target.checked }))} />}
                        label={esTrib ? 'Demanda (con certificado DGR adjunto)' : 'Demanda'}
                    />
                    <FormControlLabel control={<Checkbox checked={tiposSel.RECIBO} onChange={e => setTiposSel(t => ({ ...t, RECIBO: e.target.checked }))} />} label="Recibo" />
                    <Box sx={{ flex: 1 }} />
                    <Button variant="outlined" onClick={exportarExcelDocumentos} startIcon={<FileDownloadIcon />}
                        sx={{ textTransform: 'none', fontWeight: 600, borderColor: COLOR, color: COLOR }}>
                        Exportar listado (Excel)
                    </Button>
                    <FormControlLabel control={<Checkbox checked={soloFaltantes} onChange={e => setSoloFaltantes(e.target.checked)} />} label="Solo faltantes" />
                    <Button variant="contained" onClick={handleGenerar} disabled={generando}
                        sx={{ bgcolor: COLOR, '&:hover': { bgcolor: '#4a148c' }, textTransform: 'none', fontWeight: 600 }}>
                        {generando ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Generar ahora'}
                    </Button>
                </Box>
                {generando && progreso && (
                    <ProgresoGeneracion progreso={progreso} ahora={ahora}
                        actual={{ CARTA_PODER: conCartaPoder, DEMANDA: conDemanda, RECIBO: conRecibo }} />
                )}
            </Paper>

            {/* Panel firma digital */}
            {conCartaPoder > 0 && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: `${BR}px`, bgcolor: '#eceff1' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <DrawIcon sx={{ color: '#37474f' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#37474f' }}>
                            Firma digital de cartas poder
                        </Typography>
                        <Chip size="small" label={`Sin enviar: ${resumenFirma.sinEnviar || 0}`} variant="outlined" />
                        <Chip size="small" label={`En firma: ${resumenFirma.enFirma || 0}`} color={resumenFirma.enFirma > 0 ? 'warning' : 'default'} />
                        <Chip size="small" label={`Firmadas: ${resumenFirma.firmadas || 0}`} color={resumenFirma.firmadas > 0 ? 'success' : 'default'} />
                        <Box sx={{ flex: 1 }} />
                        {resumenFirma.sinEnviar > 0 && (
                            <Button size="small" variant="contained" startIcon={<SendIcon />}
                                onClick={handleEnviarAFirma} disabled={enviandoFirma}
                                sx={{ bgcolor: '#37474f', '&:hover': { bgcolor: '#263238' }, textTransform: 'none', fontWeight: 600 }}>
                                {enviandoFirma ? <CircularProgress size={18} sx={{ color: 'white' }} /> : `Enviar ${resumenFirma.sinEnviar} a firma`}
                            </Button>
                        )}
                        {resumenFirma.enFirma > 0 && (
                            <Button size="small" variant="outlined" startIcon={<SyncIcon />}
                                onClick={handleSincronizar} disabled={sincronizando}
                                sx={{ color: '#37474f', borderColor: '#37474f', textTransform: 'none' }}>
                                {sincronizando ? <CircularProgress size={16} /> : 'Sincronizar'}
                            </Button>
                        )}
                    </Box>
                    {resumenFirma.enFirma > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Auto-sincroniza cada 15 segundos. El Fiscal firma desde <strong>Panel de Firma → tab Carga Masiva</strong>.
                        </Typography>
                    )}
                </Paper>
            )}

            {/* Panel firma demandas (circular por abogados apoderados) */}
            {conDemanda > 0 && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: `${BR}px`, bgcolor: '#fff3e0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <DrawIcon sx={{ color: '#e65100' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#e65100' }}>
                            Firma de demandas por abogados apoderados
                        </Typography>
                        <Chip size="small" label={`Sin enviar: ${resumenFirmaDem.sinEnviar || 0}`} variant="outlined" />
                        <Chip size="small" label={`En circulación: ${resumenFirmaDem.enFirma || 0}`} color={resumenFirmaDem.enFirma > 0 ? 'warning' : 'default'} />
                        <Chip size="small" label={`Completas: ${resumenFirmaDem.firmadas || 0}`} color={resumenFirmaDem.firmadas > 0 ? 'success' : 'default'} />
                        <Box sx={{ flex: 1 }} />
                        {resumenFirmaDem.sinEnviar > 0 && (
                            <Button size="small" variant="contained" startIcon={<SendIcon />}
                                onClick={handleEnviarDemandasAFirma} disabled={enviandoFirmaDem}
                                sx={{ bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' }, textTransform: 'none', fontWeight: 600 }}>
                                {enviandoFirmaDem ? <CircularProgress size={18} sx={{ color: 'white' }} /> : `Enviar ${resumenFirmaDem.sinEnviar} a firma`}
                            </Button>
                        )}
                        {resumenFirmaDem.enFirma > 0 && (
                            <Button size="small" variant="outlined" startIcon={<SyncIcon />}
                                onClick={handleSincronizarDem} disabled={sincronizandoDem}
                                sx={{ color: '#e65100', borderColor: '#e65100', textTransform: 'none' }}>
                                {sincronizandoDem ? <CircularProgress size={16} /> : 'Sincronizar'}
                            </Button>
                        )}
                    </Box>
                    {resumenFirmaDem.enFirma > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Las demandas circulan por cada apoderado en orden (principal → co-apoderados).
                            Cada abogado firma desde su <strong>Panel de Firma</strong> propio. Auto-sincroniza cada 15s.
                        </Typography>
                    )}
                </Paper>
            )}

            {ultimoResultado && (
                <Alert severity={ultimoResultado.errores?.length > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                    Generados: <strong>{ultimoResultado.cartaPoder || 0}</strong> cartas poder,
                    <strong> {ultimoResultado.demanda || 0}</strong> demandas,
                    <strong> {ultimoResultado.recibo || 0}</strong> recibos.
                    {ultimoResultado.errores?.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>Errores:</Typography>
                            <ul style={{ margin: '4px 0', paddingLeft: 18, fontSize: '0.8rem' }}>
                                {ultimoResultado.errores.slice(0, 10).map((e, i) => (
                                    <li key={i}>Fila {e.fila} ({e.tipo}): {e.error}</li>
                                ))}
                                {ultimoResultado.errores.length > 10 && <li>... y {ultimoResultado.errores.length - 10} más</li>}
                            </ul>
                        </Box>
                    )}
                </Alert>
            )}

            {/* Tabla por fila */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress sx={{ color: COLOR }} />
                </Box>
            ) : filas.length === 0 ? (
                <Alert severity="info">No hay filas con abogado asignado.</Alert>
            ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 480px)' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow sx={{ '& th': { bgcolor: '#ede7f6', fontWeight: 700, color: COLOR } }}>
                                <TableCell>#</TableCell>
                                <TableCell>Apellido / Razón</TableCell>
                                <TableCell>Abogado</TableCell>
                                <TableCell>Cert.</TableCell>
                                <TableCell align="right">Monto</TableCell>
                                <TableCell align="center">Carta Poder</TableCell>
                                <TableCell align="center">Demanda</TableCell>
                                <TableCell align="center">Recibo</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filas.map(f => (
                                <TableRow key={f.Id} hover>
                                    <TableCell>{f.Fila}</TableCell>
                                    <TableCell>{f.Razon_Social} {f.Nombre && `, ${f.Nombre}`}</TableCell>
                                    <TableCell sx={{ fontSize: '0.8rem' }}>{f.Abogado_Nombre}</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>{f.Certificado_Serie}-{f.Certificado_Numero}</TableCell>
                                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{Number(f.Monto_Total || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</TableCell>
                                    <TableCell align="center">
                                        {f.Ruta_PDF_Carta_Poder ? (
                                            <Tooltip title={`N° ${f.Carta_Poder_Numero}/${f.Carta_Poder_Ano}`}>
                                                <IconButton size="small" onClick={() => descargar(f.Id, 'CARTA_PODER', f.Fila)}>
                                                    <PdfIcon fontSize="small" sx={{ color: '#5e35b1' }} />
                                                </IconButton>
                                            </Tooltip>
                                        ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                                    </TableCell>
                                    <TableCell align="center">
                                        {f.Ruta_PDF_Demanda ? (
                                            <Tooltip title={esTrib ? 'Ver demanda (incluye certificado DGR)' : 'Ver demanda'}>
                                                <IconButton size="small" onClick={() => descargar(f.Id, 'DEMANDA', f.Fila)}>
                                                    <PdfIcon fontSize="small" sx={{ color: '#c2185b' }} />
                                                </IconButton>
                                            </Tooltip>
                                        ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                                    </TableCell>
                                    <TableCell align="center">
                                        {f.Ruta_PDF_Recibo ? (
                                            <Tooltip title="Ver recibo (compartido con otras filas del mismo abogado)">
                                                <IconButton size="small" onClick={() => descargar(f.Id, 'RECIBO', f.Fila)}>
                                                    <PdfIcon fontSize="small" sx={{ color: '#00897b' }} />
                                                </IconButton>
                                            </Tooltip>
                                        ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <PdfPreviewDialog
                open={previewState.open}
                blob={previewState.blob}
                titulo={previewState.titulo}
                filename={previewState.filename}
                onClose={cerrarPreview}
            />

            {/* Dialog: filas sin CP/Demanda/Recibo */}
            <Dialog open={!!dialogFaltantes} onClose={() => setDialogFaltantes(null)} maxWidth="md" fullWidth
                PaperProps={{ sx: { borderRadius: `${BR}px` } }}>
                <DialogTitle>
                    Filas sin {dialogFaltantes?.tipo} ({dialogFaltantes?.filas?.length || 0})
                </DialogTitle>
                <DialogContent>
                    {(dialogFaltantes?.filas || []).length === 0 ? (
                        <Alert severity="success">Todas las filas tienen {dialogFaltantes?.tipo}.</Alert>
                    ) : (
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ '& th': { bgcolor: '#ede7f6', fontWeight: 700, color: COLOR } }}>
                                        <TableCell>#</TableCell>
                                        <TableCell>Demandado</TableCell>
                                        <TableCell>Certificado</TableCell>
                                        <TableCell>Abogado</TableCell>
                                        <TableCell>Carta Poder Nº</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(dialogFaltantes?.filas || []).map(f => {
                                        const prefCp = lote.Id_Modulo === 1 ? 'A' : 'B';
                                        const cp = f.Carta_Poder_Numero
                                            ? `${prefCp}-${String(f.Carta_Poder_Numero).padStart(6, '0')}/${String(f.Carta_Poder_Ano).padStart(2, '0')}`
                                            : '—';
                                        return (
                                            <TableRow key={f.Id} hover>
                                                <TableCell>{f.Fila}</TableCell>
                                                <TableCell>{f.Razon_Social}{f.Nombre && `, ${f.Nombre}`}</TableCell>
                                                <TableCell sx={{ fontFamily: 'monospace' }}>{f.Certificado_Serie}-{f.Certificado_Numero}</TableCell>
                                                <TableCell>{f.Abogado_Nombre || '—'}</TableCell>
                                                <TableCell sx={{ fontFamily: 'monospace' }}>{cp}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogFaltantes(null)}>Cerrar</Button>
                    {dialogFaltantes?.filas?.length > 0 && (
                        <Button variant="contained" onClick={() => { setDialogFaltantes(null); setSoloFaltantes(true); }}
                            sx={{ bgcolor: COLOR, '&:hover': { bgcolor: '#4a148c' } }}>
                            Activar "Solo faltantes" para regenerar
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
}

